import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import styles from "./UnitProgressModal.module.scss";
import {
  Program,
  Lesson,
  ExitTicketActive,
  FeedbackForm,
  ModeTypeEnum,
  ExtendedHeaUnit,
  LessonStatus,
} from "@types";
import Button from "src/components/button/Button";
import UnitModal from "./modal/UnitModal";
import CoverVideoPlayer from "./cover-video/VideoPlayer";
import { getMediaAbsolutePath } from "../../services/File";
import BookMarkIcon from "../../assets/icons/bookmark.svg?react";
import CompleteIcon from "../../assets/icons/completed_check.svg?react";
import FSBackIcon from "@src/assets/icons/arrow_back.svg?react";
import GreenShareIcon from "src/assets/icons/share-green.svg?react";
import ShareIcon from "src/assets/icons/new-share.svg?react";
import {
  ExtendedUnitFragment,
  useGetUnitLazyQuery,
  useUpdateProgramLastUsedMutation,
  useGetLasVisitedLessonByProgramIdLazyQuery,
  useHeaGetUnitInfoLazyQuery,
  LessonFragment,
  useHeaUnlockAccountMutation,
  ContentType,
  useHeaGetLessonStatusLazyQuery,
  StudyPlanContentFragment,
  FeedbackFormContent,
  useCancelFeedbackFormMutation,
  useHeaConfirmLessonMutation,
  SectionFragment,
  useSwitchLessonFlowMutation,
  useGetPreProgramQuizUnitLazyQuery,
  useLastVisitedUnitStudyReviewLazyQuery,
  useGetStudyReviewUnitLazyQuery,
  Content,
  useGetPostProgramQuizUnitLazyQuery,
  useGetAllProgramsLazyQuery,
  AbsentStudentLesson,
} from "../../api/graphql-codegen";
import Spinner from "../spinner/Spinner";
import OverviewForm from "./overview-form/OverviewForm";
import TeacherForm from "./teacher-form/TeacherForm";
import LessonForm from "./lesson-form/LessonForm";
import TeacherDetailView from "./teacher-form/detail/TeacherDetailView";
import {
  CourseContentProvider,
  useCourseContent,
} from "@hooks/useCourseContent";
import SelectedContent from "../program-details/selected-content/SelectedContent";
import LessonPreView from "./lesson-form/detail/LessonPreView";
import ResumeModal from "./resume-modal/ResumeModal";
import {
  useAnalytics,
  Events,
  useHeaUser,
  useToast,
  useExitTicketLocalStorage,
  useUser,
} from "@hooks";
import {
  CreatingResilientAusClassroomsId,
  CreatingResilientClassroomsId,
  GroupOnboardingId,
  WellBeingUnitId,
  createPrettyURL,
  createStudentLink,
  isHomeEDApp,
  parentPlanName,
} from "@constants";
import { Tooltip } from "antd";
import NewPasswordModal from "../program-details/password-modal/NewPasswordModal";
import SuccessModal from "../program-details/success-modal/SuccessModal";
import { useAuthToken } from "@hooks/useAuthToken";
import LockModal from "../program-details/lock-modal/LockModal";
import CloseIcon from "../../assets/icons/close.svg?react";
import LockIcon from "@src/assets/icons/lockaccount.svg?react";
import { TextInput, TextInputValue } from "../text-input/TextInput";
import LessonRunModal from "../program-details/lesson-run-modal/LessonRunModal";
import GenerateExitTicketLinkModal from "../program-details/exitticket-modal/GenerateExitTicketLinkModal";
import { Widget } from "@typeform/embed-react";
import { notEmpty } from "@utils/helpers";
import ConfirmPasswordModal from "./confirm-modal/ConfirmPasswordModal";
import ReviewModal from "./review-modal/ReviewModal";
import CompletedLessonsModal from "./CompletedLessonsModal";
import MultiLessonRunModal from "../program-details/lesson-run-modal/MultiLessonRunModal";
import AlternateLessonRun from "../program-details/alternate-lesson-modal/AlternateLessonRun";
import AdditionalExerciseModal from "../program-details/additional-exercise-modal/AdditionalExerciseModal";
import InstructionLessonRun from "../lesson-details/instruction-modal/InstructionLessonRun";
import { useHistory, useLocation } from "react-router-dom";
import StudyGuide from "./studyreview/StudyGuide";
import StudyGuidePreView from "./studyreview/detail/StudyGuidePreView";
import MaterialPreview from "./material-form/MaterialPreview";
import NormalModal from "../modal/normal-modal/NormalModal";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ShareModal from "../modal/share-modal/ShareModal";
import NewLayoutLessonForm from "./lesson-form/NewLayoutLessonForm";
import GenerateStudentLinkModal from "../studentlink-modal/GenerateStudentLinkModal";
import RequiredUnitLessonMaterials from "../lesson-details/lessonmaterials-modal/RequiredUnitLessonMaterials";
import { usePresentationContext } from "@src/hooks/usePresentationContext";
import { logger } from "../../utils/logger";

// Type assertion for CopyToClipboard to fix TypeScript error
const CopyToClipboardComponent = CopyToClipboard as React.ComponentType<any>;

type Props = {
  open: boolean;
  onClose: (isCRC?: boolean | null | undefined) => void;
  unitId: string;
  program?: Program;
  unitData?: ExtendedUnitFragment;
  heaUnitData?: ExtendedHeaUnit;
  isDashboard?: boolean;
  isParentApp?: boolean;
  parentSkill?: string;
  isEditMode?: boolean;
  studyPlansForSelectedUnit?: StudyPlanContentFragment[];
  addUnitToStudyPlan?: () => void;
  studyMode?: string;
  currentStudyPlan?: StudyPlanContentFragment;
  gotoStudyMode?: (studyPlan: StudyPlanContentFragment) => void;
  studyPlans?: StudyPlanContentFragment[];
  heaUnits?: ExtendedHeaUnit[];
  assessments?: boolean;
  quizUnit?: boolean;
  lessonInfoByUniqueName?: any;
  isPostProgram?: boolean;
};

function sortByNumber(
  a: { number?: number | null } | null,
  b: { number?: number | null } | null,
) {
  return (a?.number ?? 10000) - (b?.number ?? 0);
}

function selectFirstContentExceptSurvey(modules: any[]) {
  if (modules.length > 0) {
    if (modules.at(0).type === ContentType.FORM) {
      modules.shift();
      return selectFirstContentExceptSurvey(modules);
    } else {
      return modules.at(0);
    }
  } else {
    return undefined;
  }
}

const createURL = (id: string) => {
  return `${window.location.origin}/ff/${id}`;
};

function UnitProgressComponent(props: Props) {
  const {
    open,
    onClose,
    unitId,
    unitData,
    heaUnitData,
    program,
    isDashboard,
    isParentApp,
    isEditMode = false,
    parentSkill,
    studyPlansForSelectedUnit,
    addUnitToStudyPlan,
    studyMode,
    currentStudyPlan,
    gotoStudyMode,
    heaUnits,
    studyPlans,
    assessments = false,
    quizUnit = false,
    lessonInfoByUniqueName,
    isPostProgram,
  } = props;
  const isGroupOnboarding = unitId === GroupOnboardingId;
  const [showTeachView, setShowTeachView] = useState(false);
  const [showLessonView, setShowLessonView] = useState(false);
  const [showLessonPreView, setShowLessonPreView] = useState(false);
  const [showLessonFeedback, setShowLessonFeedback] = useState(false);
  const [resumeModal, setResumeModal] = useState(false);
  const [lastPageNum, setLastPageNum] = useState(1);
  const [getBack, setGetBack] = useState(false);
  const [isInViewport, SetIsInViewport] = useState(true);
  const [backHover, setBackHover] = useState(false);
  const { trackEvent } = useAnalytics();
  const { successToast } = useToast();
  const { getFormId, setFormId, removeFormId } = useExitTicketLocalStorage();

  const { password, setLockAccount, setParentFirstLogin } = useAuthToken();
  const [activeModal, setActiveModal] = useState(false);
  const [activated, setActivated] = useState(false);
  const [lockModal, setLockModal] = useState(false);
  const [lessonRunModal, setLessonRunModal] = useState(false);
  const [alternateLessonRunModal, setAlternateLessonRunModal] = useState(false);
  const [state, setState] = useState<number>(1);

  const [locked, setLocked] = useState(false);
  const [studyplanName, setStudyplanName] = useState("");
  const [serverError, setServerError] = useState("");
  const [movedScroll, setMovedScroll] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [showPasscodeBox, setShowPasscodeBox] = useState(false);
  const [showConfirmCompleteModal, setShowConfirmCompleteModal] =
    useState(false);
  const [lessonConfirm, setLessonConfirm] = useState(false);
  const [reviewLesson, setReviewLesson] = useState(false);
  const [isFromPreview, setIsFromPreview] = useState(false);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [showGuideSessionModal, setShowGuideSessionModal] = useState(false);

  const [showPendingLessons, setShowPendingLessons] = useState(false);
  const [confirmPWDForPendingLessons, setConfirmPWDForPendingLessons] =
    useState(false);
  const [formData, setFormData] = useState<string | undefined>(undefined);

  const [showStudyGuidePreview, setShowStudyGuidePreview] = useState(false);
  const [isQuizUnit, setQuizUnit] = useState(quizUnit);
  const [startResume, setStartResume] = useState(false);

  const [showMaterail, setShowMaterial] = useState(false);
  // const [showMaterailView, setShowMaterialView] = useState(false);
  const [needPrint, setNeedPrint] = useState(false);

  const [sharedObject, setSharedObject] = useState<any | undefined>(undefined);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [shareLesson, setShareLesson] = useState(false);
  const [showStudentLinkModal, setShowStudentLinkModal] = useState(false);

  let countDownTimer = useRef<NodeJS.Timeout | null>(null);

  const history = useHistory();
  const { pathname } = useLocation();

  const [user] = useUser();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [heaUser, _, finishStudyPlan] = useHeaUser();
  const {
    program: currentProgram,
    setProgram,
    currentUnit,
    setCurrentUnit,
    setCurrentHeaUnit,
    content: selectedContent,
    lesson,
    setContent,
    setLesson,
    setIsParent,
    setIsPreview,
    // accountLocked,
    setAccountLocked,
    exitTickets,
    setExitTickets,
    showExitModal,
    setShowExitModal,
    currentStudyplan,
    setCurrentStudyplan,
    setConfirmedPassword,
    isViewExitTicket,
    setIsViewExitTicket,
    showReview,
    setShowReview,
    section,
    setSection,
    showExerciseModal,
    setShowExerciseModal,
    startedAlternateLesson,
    setStartedAlternateLesson,
    setIsStudyGuide,
    isStudyGuide,
    setCompleteModal,
    setIsNewLessonLayout,
  } = useCourseContent();
  const [switchLessonFlow] = useSwitchLessonFlowMutation();
  const { setIsPreview: setContextPreview } = usePresentationContext();

  const studyPlan = useMemo(
    () => currentStudyPlan || currentStudyplan,
    [currentStudyPlan, currentStudyplan],
  );

  useEffect(() => {
    if (heaUser) {
      setLocked(!!heaUser?.locked);
      setAccountLocked(!!heaUser?.locked);
      setShowPasscodeBox(false);
      setPasscode("");
      if (heaUser?.locked) {
        setStudyplanName(heaUser.studyplanName ?? "");
        if (heaUser?.studyplanId) {
          const selectedStudyPlan = (studyPlansForSelectedUnit || []).find(
            (s) => s.id === heaUser?.studyplanId,
          );
          if (selectedStudyPlan) {
            setCurrentStudyplan(selectedStudyPlan);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heaUser, setAccountLocked]);

  const [getAllPrograms] = useGetAllProgramsLazyQuery({
    fetchPolicy: "cache-first",
  });

  const [getUnitInfo, { loading, data }] = useGetUnitLazyQuery({
    fetchPolicy: "network-only",
    onCompleted() {
      if (getBack) {
        setTimeout(() => {
          handleGoLessons();
        }, 300);
      }
    },
  });
  const [
    getStudyReviewUnit,
    { loading: studyReviewLoading, data: studyReviewData },
  ] = useGetStudyReviewUnitLazyQuery({
    fetchPolicy: "network-only",
  });

  const [heaGetUnitInfo, { loading: loading2, data: data2 }] =
    useHeaGetUnitInfoLazyQuery({
      fetchPolicy: "network-only",
      onCompleted() {
        if (getBack) {
          setTimeout(() => {
            handleGoLessons();
          }, 300);
        }
      },
    });

  const [getPreProgramQuizUnit, { loading: loading3, data: data3 }] =
    useGetPreProgramQuizUnitLazyQuery({ fetchPolicy: "network-only" });

  const [getPostProgramQuizUnit, { loading: loading4, data: data4 }] =
    useGetPostProgramQuizUnitLazyQuery({ fetchPolicy: "network-only" });

  const [getLastVisitedUnitStudyReview, { data: LasVisitedStudyGuide }] =
    useLastVisitedUnitStudyReviewLazyQuery({
      fetchPolicy: "network-only",
    });
  const [
    getLasVisitedLessonByProgramId,
    { data: LasVisitedLesson, loading: visitloading },
  ] = useGetLasVisitedLessonByProgramIdLazyQuery({
    fetchPolicy: "network-only",
  });
  const [updateLastUsed] = useUpdateProgramLastUsedMutation({
    onError(e) {
      logger.debug(`[e]`, e);
    },
  });
  const [
    getHeaLessonStatus,
    { data: heaLessonsStatus, loading: getHeaLessonStatusLoading },
  ] = useHeaGetLessonStatusLazyQuery({ fetchPolicy: "network-only" });
  const [cancelFeedbackForm] = useCancelFeedbackFormMutation();

  const getHeaLessonCompletedStatus = useCallback(async () => {
    if (studyPlan?.id && unitId && isHomeEDApp) {
      await getHeaLessonStatus({
        variables: {
          input: {
            studyplanId: studyPlan?.id,
            unitId: unitId,
          },
        },
      });
    }
  }, [getHeaLessonStatus, studyPlan?.id, unitId]);
  const [heaConfirmLesson, { loading: confirming }] =
    useHeaConfirmLessonMutation();

  const backTitle = useMemo(() => {
    if (pathname === "/dashboard") {
      return "Return to Dashboard";
    } else if (pathname.includes("/programs")) {
      return "Return to Programs";
    } else {
      return "Go Back";
    }
  }, [pathname]);

  const curUnitClassPresentation = useMemo(() => {
    if (data3?.getPreProgramQuizUnit) {
      return (
        data3?.getPreProgramQuizUnit &&
        data3?.getPreProgramQuizUnit.classPresentation
      );
    } else if (data4?.getPostProgramQuizUnit) {
      return (
        data4?.getPostProgramQuizUnit &&
        data4?.getPostProgramQuizUnit.classPresentation
      );
    }
    return data?.getUnitInfo && data?.getUnitInfo.classPresentation;
  }, [
    data?.getUnitInfo,
    data3?.getPreProgramQuizUnit,
    data4?.getPostProgramQuizUnit,
  ]);

  useEffect(() => {
    if (curUnitClassPresentation && !getBack) {
      var tickets: ExitTicketActive[] = [];
      tickets = curUnitClassPresentation.map((lesson) => {
        let formContent = lesson?.modules?.find(
          (module) => module.type === ContentType.FORM,
        );
        if (formContent && formContent.formData) {
          const jsonFormData = JSON.parse(formContent.formData) as FeedbackForm;
          if (jsonFormData) {
            return {
              lessonNum: lesson?.number ?? 1,
              formData: formContent.formData,
              status: false,
              type: Number(jsonFormData?.surveyType),
              exitTicketCount: 0,
              expiryMinutes: 0,
              copyLink: "",
              code: "",
            };
          } else {
            return {
              lessonNum: lesson?.number ?? 1,
              formData: null,
              status: false,
              type: 0,
              exitTicketCount: 0,
              expiryMinutes: 0,
              copyLink: "",
              code: "",
            };
          }
        } else {
          return {
            lessonNum: lesson?.number ?? 1,
            formData: null,
            status: false,
            type: 0,
            exitTicketCount: 0,
            expiryMinutes: 0,
            copyLink: "",
            code: "",
          };
        }
      });
      setExitTickets(tickets);
    }
  }, [curUnitClassPresentation, getBack, setExitTickets]);

  useEffect(() => {
    const existExpires = exitTickets.filter(
      (ticket) => ticket.expiryMinutes > 0,
    );
    if (existExpires.length > 0) {
      countDownTimer.current = setInterval(() => {
        const tickets = exitTickets.map((ticket) => {
          if (ticket.expiryMinutes > 0) {
            return {
              ...ticket,
              expiryMinutes: ticket.expiryMinutes - 60,
              status: ticket.expiryMinutes - 60 >= 0 ? true : false,
            };
          }
          return ticket;
        });
        setExitTickets(tickets);
      }, 60 * 1000); // countdown every 1 min
    }
    return () => {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current);
        countDownTimer.current = null;
      }
    };
  }, [exitTickets, setExitTickets]);

  useEffect(() => {
    if (
      (!unitData?.teacherPreparation || !unitData?.classPresentation) &&
      unitId &&
      !isHomeEDApp
    ) {
      if (quizUnit) {
        getStudyReviewUnit({
          variables: { input: { id: unitId } },
        });
      } else {
        getUnitInfo({
          variables: { input: { id: unitId } },
        });
      }
    } else if (
      (!heaUnitData?.parentPreparation || !heaUnitData?.classPresentation) &&
      unitId &&
      isHomeEDApp
    ) {
      heaGetUnitInfo({
        variables: { input: { id: unitId } },
      });
      if (currentStudyPlan?.id) {
        getHeaLessonCompletedStatus();
      }
    } else {
      if (assessments) {
        getPreProgramQuizUnit();
      } else if (isPostProgram) {
        getPostProgramQuizUnit();
      }
    }
  }, [
    assessments,
    isPostProgram,
    getPreProgramQuizUnit,
    getUnitInfo,
    unitId,
    unitData,
    heaUnitData?.parentPreparation,
    heaUnitData?.classPresentation,
    heaGetUnitInfo,
    currentStudyPlan?.id,
    getHeaLessonCompletedStatus,
    getStudyReviewUnit,
    quizUnit,
    getPostProgramQuizUnit,
  ]);

  const curUnitContent = useMemo(() => {
    if (data?.getUnitInfo) {
      return data?.getUnitInfo;
    } else if (data3?.getPreProgramQuizUnit) {
      return data3?.getPreProgramQuizUnit;
    } else if (data4?.getPostProgramQuizUnit) {
      return data4.getPostProgramQuizUnit;
    } else if (studyReviewData?.getUnitInfo) {
      return studyReviewData?.getUnitInfo;
    } else {
      return unitData;
    }
  }, [
    data?.getUnitInfo,
    unitData,
    data3?.getPreProgramQuizUnit,
    studyReviewData,
    data4?.getPostProgramQuizUnit,
  ]);

  const curUnitContent2 = useMemo(() => {
    const extendedUnit = data2?.heaGetUnitInfo || heaUnitData;
    return extendedUnit;
  }, [data2?.heaGetUnitInfo, heaUnitData]);

  const curExtendedUnit2: ExtendedHeaUnit | undefined = useMemo(
    () => data2?.heaGetUnitInfo || heaUnitData,
    [data2?.heaGetUnitInfo, heaUnitData],
  );

  const curExtendedUnit = useMemo(
    () => (isHomeEDApp ? curExtendedUnit2 : curUnitContent),
    [curUnitContent, curExtendedUnit2],
  );

  const studyGuides = useMemo(
    () => [...(curUnitContent?.studyGuides || [])].filter(notEmpty),
    [curUnitContent?.studyGuides],
  );

  const newLessonLayout = useMemo(
    () => curUnitContent?.isEducatorCaregiverLessonLayout,
    [curUnitContent],
  );

  // useEffect(() => {
  //   if (newLessonLayout && curUnitContent?.uniqueName && pathname === '/dashboard') {
  //     history.replace(`/${curUnitContent?.uniqueName}`);
  //   }
  // }, [newLessonLayout, curUnitContent?.uniqueName, pathname, history])

  useEffect(() => {
    if (
      !isParentApp &&
      !isHomeEDApp &&
      curExtendedUnit?.id &&
      studyGuides.length > 0
    ) {
      getLastVisitedUnitStudyReview({
        variables: {
          input: { id: curExtendedUnit.id },
        },
      });
    }
  }, [
    getLastVisitedUnitStudyReview,
    isParentApp,
    curExtendedUnit?.id,
    studyGuides,
  ]);

  useEffect(() => {
    if (!isParentApp && !isHomeEDApp && unitId) {
      getLasVisitedLessonByProgramId({
        variables: {
          input: { id: unitId },
        },
      });
      if (isDashboard && !isParentApp && program?.id) {
        updateLastUsed({
          variables: {
            input: {
              id: program.id,
            },
          },
        });
      }
    }
  }, [
    program?.id,
    unitId,
    updateLastUsed,
    getLasVisitedLessonByProgramId,
    isDashboard,
    isParentApp,
  ]);

  useEffect(() => {
    if (!isParentApp && !isHomeEDApp && assessments && curExtendedUnit?.id) {
      getLasVisitedLessonByProgramId({
        variables: {
          input: { id: curExtendedUnit?.id },
        },
      });
    }
  }, [
    getLasVisitedLessonByProgramId,
    isParentApp,
    assessments,
    curExtendedUnit?.id,
  ]);

  useEffect(() => {
    setIsParent(!!isParentApp);
  }, [isParentApp, setIsParent]);

  useEffect(() => {
    if (curUnitContent) {
      setCurrentUnit(curUnitContent);
    }
  }, [curUnitContent, setCurrentUnit]);

  useEffect(() => {
    if (curExtendedUnit2) {
      setCurrentHeaUnit(curExtendedUnit2);
    }
  }, [curExtendedUnit2, setCurrentHeaUnit]);

  useEffect(() => {
    if (newLessonLayout) {
      setIsNewLessonLayout(true);
    } else {
      setIsNewLessonLayout(false);
    }
  }, [newLessonLayout, setIsNewLessonLayout]);

  useEffect(() => {
    if (program) {
      setProgram(program);
    } else {
      const fetchData = async () => {
        const res = await getAllPrograms();
        return res.data?.programs;
      };

      fetchData()
        .then((res) => {
          if (res && curUnitContent) {
            const courses = JSON.parse(curUnitContent.courseId || "[]");
            const filterProgs = res.filter((p) => p?.id === courses[0]);
            if (filterProgs[0]) {
              setProgram(filterProgs[0] as Program);
            }
          }
        })
        .catch(console.error);
    }
  }, [program, setProgram, curUnitContent, getAllPrograms]);

  const handleScroll = useCallback(() => {
    const element = document.getElementById("coverView");
    if (element) {
      const rect = element.getBoundingClientRect();

      const isVisible =
        rect.top >= -(rect.height / 2) &&
        rect.height / 2 <= rect.bottom &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight);

      if (!isVisible && isInViewport) {
        SetIsInViewport(false);
      }
      if (isVisible && !isInViewport) {
        SetIsInViewport(true);
      }
    }
    setMovedScroll((current) => !current);
  }, [isInViewport]);

  const handleClose = useCallback(() => {
    setGetBack(false);
    SetIsInViewport(true);
    setContextPreview(false);
    videoPlayerRef.current?.setMute(true);
    if (
      showTeachView ||
      showLessonView ||
      resumeModal ||
      showLessonPreView ||
      showStudyGuidePreview ||
      showMaterail
    ) {
      setResumeModal(false);
      setShowTeachView(false);
      setShowLessonView(false);
      setShowLessonPreView(false);
      setShowStudyGuidePreview(false);
      setIsStudyGuide(false);
      setCompleteModal(false);
      setShowMaterial(false);
      document.getElementById("modal-body")!.style.overflow = "auto";
    } else {
      if (pathname !== "/program/studentvoiceseries") {
        history.replace(`${pathname}`);
      }
      onClose(newLessonLayout);
    }
  }, [
    setContextPreview,
    showTeachView,
    showLessonView,
    resumeModal,
    showLessonPreView,
    showStudyGuidePreview,
    showMaterail,
    setIsStudyGuide,
    setCompleteModal,
    pathname,
    onClose,
    newLessonLayout,
    history,
  ]);

  const handleOpenTeacher = useCallback(() => {
    if (isStudyGuide) setIsStudyGuide(false);
    setShowInstructionModal(false);
    setShowLessonPreView(false);
    setShowGuideSessionModal(false);
    setShowStudyGuidePreview(false);
    const teachform = document.getElementById("teachform");
    teachform?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setShowTeachView(true);
    setIsPreview(true);
    if (newLessonLayout) {
      setIsNewLessonLayout(false);
    }
    trackEvent(Events.actions.lessonPreviewOpen, {
      unit_name: currentUnit?.name || "",
      lesson_name: lesson?.name,
      course_name: program?.name || currentProgram?.name || "",
      lesson_number: lesson?.number,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [
    currentUnit?.name,
    lesson?.name,
    lesson?.number,
    program?.name,
    currentProgram?.name,
    setIsPreview,
    trackEvent,
    setShowLessonPreView,
    isStudyGuide,
    setIsStudyGuide,
    newLessonLayout,
    setIsNewLessonLayout,
    user,
  ]);

  const handleCloseTeacher = useCallback(() => {
    if (showTeachView) {
      setShowTeachView(false);
    }
    if (newLessonLayout) {
      setIsNewLessonLayout(true);
    }
  }, [showTeachView, newLessonLayout, setIsNewLessonLayout]);

  const handleOpenStudyGuidePreview = useCallback(() => {
    setLesson(undefined);
    setShowInstructionModal(false);
    setShowLessonPreView(false);
    setShowStudyGuidePreview(true);
    setIsPreview(true);
    setIsStudyGuide(true);
  }, [setIsPreview, setLesson, setShowLessonPreView, setIsStudyGuide]);

  const handleCloseStudyGuidePreview = useCallback(() => {
    setShowStudyGuidePreview(false);
    setIsStudyGuide(false);
  }, [setIsStudyGuide]);

  const handleStartStudyGuide = useCallback(() => {
    setIsViewExitTicket(false);
    setShowLessonPreView(false);

    if (!showGuideSessionModal) {
      setShowGuideSessionModal(true);
      return;
    }
    setIsPreview(false);
    setLesson(undefined);
    setShowGuideSessionModal(false);
    setIsStudyGuide(true);

    setTimeout(() => {
      const index = studyGuides.findIndex(
        (m) =>
          m?.id ===
          LasVisitedStudyGuide?.lastVisitedUnitStudyReview?.[0]?.moduleId,
      );
      if (index > -1) {
        setResumeModal(true);
      } else {
        if (studyGuides) {
          setContent(studyGuides.at(0) as any);
          setLastPageNum(1);
        }
        setShowStudyGuidePreview(false);
        setShowLessonView(true);
      }
    }, 200);
  }, [
    setIsViewExitTicket,
    setContent,
    setLesson,
    setIsStudyGuide,
    setIsPreview,
    showGuideSessionModal,
    studyGuides,
    LasVisitedStudyGuide?.lastVisitedUnitStudyReview,
  ]);

  const handlerOpenParentOverview = useCallback(() => {
    const teachform = document.getElementById("teachform");
    teachform?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setTimeout(() => {
      setIsPreview(true);
      trackEvent(Events.actions.lessonPreviewOpen, {
        unit_name: currentUnit?.name || "",
        lesson_name: lesson?.name,
        course_name: program?.name || currentProgram?.name || "",
        lesson_number: lesson?.number,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
      setShowTeachView(true);
    }, 500);
  }, [
    currentUnit?.name,
    lesson?.name,
    lesson?.number,
    program?.name,
    currentProgram?.name,
    setIsPreview,
    trackEvent,
    user,
  ]);

  // Activate modal actions
  const handleSetPassword = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto";
    setActiveModal(false);
    if (lesson) {
      setLessonRunModal(true);
    } else {
      setLockModal(true);
    }
    setState(1);
    setActivated(false);
    setShowPasscodeBox(false);
    setPasscode("");
    setStudyplanName(studyPlan?.name ?? "");
    setLockAccount("lock");
  }, [setLockAccount, studyPlan?.name, lesson]);

  const handleActivated = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto";
    setActiveModal(false);
    setLockModal(false);
    setActivated(true);
    setLocked(true);
    setAccountLocked(true);
    setShowPasscodeBox(false);
    setPasscode("");
    setStudyplanName(studyPlan?.name ?? "");
    setLockAccount("lock");
  }, [setLockAccount, setAccountLocked, studyPlan?.name]);

  const handleCloseActiveModal = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto";
    setActiveModal(false);
  }, []);
  const handleCloseLockModal = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto";
    setLockModal(false);
  }, []);
  const handleActiveSuccess = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto";
    setLessonRunModal(false);
    setActivated(false);
    if (lesson) {
      setTimeout(() => {
        if (LasVisitedLesson?.lastVisitedLesson?.[0]?.lessonId === lesson.id) {
          setResumeModal(true);
        } else {
          if (lesson.modules) {
            if (isHomeEDApp) {
              setContent(selectFirstContentExceptSurvey(lesson.modules));
            } else {
              setContent(lesson.modules.at(0) as any);
            }
            setLastPageNum(1);
          }
          setShowLessonView(true);
        }
      }, 200);
    }
  }, [
    LasVisitedLesson?.lastVisitedLesson,
    setResumeModal,
    setShowLessonView,
    setContent,
    lesson,
  ]);

  const handleStudyPlanLessonOpen = useCallback(
    (lesson: Lesson, studyPlan: StudyPlanContentFragment) => {
      setLesson(lesson);
      setCurrentStudyplan(studyPlan);
      setIsViewExitTicket(false);
      // check account lock status
      if (isHomeEDApp && (!password || password === "")) {
        setActiveModal(true);
        return;
      } else if (heaUser && !heaUser?.locked) {
        setLockModal(true);
        return;
      }
      setTimeout(() => {
        if (LasVisitedLesson?.lastVisitedLesson?.[0]?.lessonId === lesson.id) {
          setResumeModal(true);
        } else {
          if (lesson.modules) {
            if (isHomeEDApp) {
              setContent(selectFirstContentExceptSurvey(lesson.modules));
            } else {
              setContent(lesson.modules.at(0) as any);
            }
            setLastPageNum(1);
          }
          setShowLessonView(true);
          trackEvent(Events.actions.lessonOpened, {
            study_plan_name: studyPlan.name,
            study_plan_id: studyPlan.id,
            unit_name: unitData?.name,
            lesson_name: lesson?.name,
            lesson_number: lesson?.number,
            schoolId: user?.schools?.[0]?.id,
            schoolName: user?.schools?.[0]?.name,
            userId: user?.id,
            userName: `${user?.firstName} ${user?.lastName}`,
          });
        }
      }, 200);
    },
    [
      setLesson,
      setCurrentStudyplan,
      password,
      heaUser,
      LasVisitedLesson?.lastVisitedLesson,
      trackEvent,
      unitData?.name,
      setContent,
      setIsViewExitTicket,
      user,
    ],
  );

  const handleOpenLessonWithChilds = useCallback(
    (lesson: Lesson) => {
      setLesson(lesson);
      setIsViewExitTicket(false);
      // check account lock status
      if (isHomeEDApp && (!password || password === "")) {
        setActiveModal(true);
        return;
      } else if (heaUser && !heaUser?.locked) {
        setState(2);
        setLessonRunModal(true);
        return;
      }
    },
    [
      heaUser,
      password,
      setLesson,
      setState,
      setActiveModal,
      setIsViewExitTicket,
    ],
  );

  const handleLockWithStudyPlan = useCallback(
    (studyPlan: StudyPlanContentFragment) => {
      const lessons = [...(curExtendedUnit?.classPresentation || [])].sort(
        sortByNumber,
      );
      const startLesson = lesson ? lesson : lessons[0];
      if (startLesson) {
        setLesson(startLesson as Lesson);
      }
      setCurrentStudyplan(studyPlan);
      setLessonRunModal(false);
      setLockModal(true);
    },
    [
      lesson,
      curExtendedUnit?.classPresentation,
      setLesson,
      setCurrentStudyplan,
    ],
  );

  const handleCloseLessonModal = useCallback(() => {
    document.getElementById("modal-body")!.style.overflow = "auto";
    setLessonRunModal(false);
  }, []);

  const handleOpenLessonWithYou = useCallback(
    (studyPlan: StudyPlanContentFragment) => {
      const lessons = [...(curExtendedUnit?.classPresentation || [])].sort(
        sortByNumber,
      );
      const startLesson = lesson ? lesson : lessons[0];
      if (!lesson) {
        setLesson(startLesson as Lesson);
      }
      setTimeout(() => {
        if (
          !!startLesson &&
          LasVisitedLesson?.lastVisitedLesson?.[0]?.lessonId === startLesson.id
        ) {
          setResumeModal(true);
        } else {
          if (startLesson?.modules) {
            // setContent(lesson.modules.at(0) as any)
            setContent(selectFirstContentExceptSurvey(startLesson.modules));
            setLastPageNum(1);
          }
          setCurrentStudyplan(studyPlan);
          setShowLessonView(true);
          setLessonRunModal(false);
          trackEvent(Events.actions.lessonOpened, {
            study_plan_name: studyPlan.name,
            study_plan_id: studyPlan.id,
            unit_name: unitData?.name,
            lesson_name: lesson?.name,
            lesson_number: lesson?.number,
            schoolId: user?.schools?.[0]?.id,
            schoolName: user?.schools?.[0]?.name,
            userId: user?.id,
            userName: `${user?.firstName} ${user?.lastName}`,
          });
        }
      }, 200);
    },
    [
      curExtendedUnit?.classPresentation,
      lesson,
      setLesson,
      LasVisitedLesson?.lastVisitedLesson,
      setCurrentStudyplan,
      trackEvent,
      unitData?.name,
      setContent,
      user,
    ],
  );

  const handleClickStudyPlan = useCallback(
    (studyPlan: StudyPlanContentFragment) => () => {
      if (studyMode === ModeTypeEnum.Study) {
        return;
      } else {
        if (gotoStudyMode) {
          gotoStudyMode(studyPlan);
        }
      }
    },
    [gotoStudyMode, studyMode],
  );
  // end StudyPlan Activate action

  // Open FullScreen Lesson flow
  const handleOpenLesson = useCallback(
    (lesson: Lesson) => {
      videoPlayerRef.current?.setMute(true);
      setLesson(lesson);
      setIsViewExitTicket(false);
      setShowLessonPreView(false);

      if (unitId === WellBeingUnitId) {
        trackEvent(Events.actions.educatorWellbeingUnitLessonOpen, {
          course_name: program?.name || currentProgram?.name,
          unit_name: unitData?.name,
          lesson_name: lesson?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      }
      if (
        lesson.sections &&
        lesson.sections.length > 0 &&
        lesson.alternativeActivated
      ) {
        setLessonRunModal(true);
        return;
      }

      if (
        lesson.materials &&
        lesson.materials.length > 0 &&
        !showInstructionModal
      ) {
        setShowTeachView(false);
        setShowInstructionModal(true);
        return;
      }
      setShowInstructionModal(false);
      setTimeout(() => {
        const index =
          lesson.modules?.findIndex(
            (m) => m?.id === LasVisitedLesson?.lastVisitedLesson?.[0]?.moduleId,
          ) ?? -1;
        if (index > -1 && startResume) {
          setContent(lesson.modules?.at(index) as any);
          setLastPageNum(
            LasVisitedLesson?.lastVisitedLesson?.[0]?.pageNumber ?? 1,
          );

          if (newLessonLayout) {
            setShowLessonPreView(true);
            setContextPreview(true);
          } else {
            setShowLessonView(true);
          }
          trackEvent(Events.actions.lessonOpened, {
            course_name: program?.name || currentProgram?.name,
            unit_name: unitData?.name,
            lesson_name: lesson?.name,
            lesson_number: lesson?.number,
            schoolId: user?.schools?.[0]?.id,
            schoolName: user?.schools?.[0]?.name,
            userId: user?.id,
            userName: `${user?.firstName} ${user?.lastName}`,
          });
        } else {
          if (lesson.modules) {
            if (isHomeEDApp) {
              setContent(selectFirstContentExceptSurvey(lesson.modules));
            } else {
              setContent(lesson.modules.at(0) as any);
            }
            setLastPageNum(1);
          }
          if (newLessonLayout) {
            setShowLessonPreView(true);
            setContextPreview(true);
          } else {
            setShowLessonView(true);
          }
          trackEvent(Events.actions.lessonOpened, {
            course_name: program?.name || currentProgram?.name,
            unit_name: unitData?.name,
            lesson_name: lesson?.name,
            lesson_number: lesson?.number,
            schoolId: user?.schools?.[0]?.id,
            schoolName: user?.schools?.[0]?.name,
            userId: user?.id,
            userName: `${user?.firstName} ${user?.lastName}`,
          });
        }
      }, 200);
    },
    [
      setLesson,
      setIsViewExitTicket,
      unitId,
      showInstructionModal,
      trackEvent,
      program?.name,
      currentProgram?.name,
      unitData?.name,
      user?.schools,
      user?.id,
      user?.firstName,
      user?.lastName,
      startResume,
      LasVisitedLesson?.lastVisitedLesson,
      setContent,
      newLessonLayout,
      setContextPreview,
    ],
  );

  // Resume Lesson from DropDown Menu
  const handleResumeMenu = useCallback(
    (lesson: Lesson) => {
      setStartResume(true);
      setTimeout(() => {
        if (LasVisitedLesson?.lastVisitedLesson && lesson?.modules) {
          const index = lesson.modules.findIndex(
            (m) => m?.id === LasVisitedLesson.lastVisitedLesson?.[0]?.moduleId,
          );
          if (index > -1) {
            setContent(lesson.modules.at(index) as any);
            setLastPageNum(
              LasVisitedLesson.lastVisitedLesson?.[0]?.pageNumber ?? 1,
            );
          } else {
            if (isHomeEDApp) {
              setContent(selectFirstContentExceptSurvey(lesson.modules));
            } else {
              setContent(lesson.modules.at(0) as any);
            }
            setLastPageNum(1);
          }
          if (isStudyGuide) setIsStudyGuide(false);
          setResumeModal(false);

          if (newLessonLayout) {
            setShowLessonPreView(true);
            setShowLessonView(false);
            setIsPreview(true);
            setContextPreview(true);
          } else {
            setIsPreview(false);
            setShowLessonPreView(false);
            setShowLessonView(true);
            setContextPreview(false);
          }
          trackEvent(
            Events.actions.resumeButtonPressed,
            {
              course_name: program?.name || currentProgram?.name,
              unit_name: unitData?.name,
              lesson_name: lesson?.name,
              lesson_number: lesson?.number,
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} ${user?.lastName}`,
            },
            true,
          );
        }
      }, 200);
    },
    [
      LasVisitedLesson?.lastVisitedLesson,
      setIsPreview,
      trackEvent,
      program?.name,
      currentProgram?.name,
      unitData,
      setContent,
      isStudyGuide,
      setIsStudyGuide,
      newLessonLayout,
      user,
      setContextPreview,
    ],
  );

  // Start Lesson from Begin on DropDown Menu
  const handleStartBeginMenu = useCallback(
    (lesson: Lesson) => {
      videoPlayerRef.current?.setMute(true);
      setStartResume(false);
      if (
        lesson.sections &&
        lesson.sections.length > 0 &&
        lesson.alternativeActivated
      ) {
        setLessonRunModal(true);
        return;
      }
      if (
        lesson.materials &&
        lesson.materials.length > 0 &&
        !showInstructionModal
      ) {
        setShowTeachView(false);
        setShowInstructionModal(true);
        return;
      }
      setShowInstructionModal(false);
      if (lesson?.modules) {
        if (isStudyGuide) setIsStudyGuide(false);
        if (isHomeEDApp) {
          setContent(selectFirstContentExceptSurvey(lesson.modules));
        } else {
          setContent(lesson.modules.at(0) as any);
        }
        setLastPageNum(1);
      }
      setResumeModal(false);
      if (newLessonLayout) {
        setIsPreview(true);
        setShowLessonPreView(true);
        setShowLessonView(false);
        setContextPreview(true);
      } else {
        setIsPreview(false);
        setShowLessonPreView(false);
        setShowLessonView(true);
        setContextPreview(false);
      }
      trackEvent(Events.actions.lessonOpened, {
        course_name: program?.name || currentProgram?.name,
        unit_name: curUnitContent?.name,
        lesson_name: lesson?.name,
        lesson_number: lesson?.number,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    },
    [
      showInstructionModal,
      newLessonLayout,
      trackEvent,
      program?.name,
      currentProgram?.name,
      curUnitContent?.name,
      user?.schools,
      user?.id,
      user?.firstName,
      user?.lastName,
      isStudyGuide,
      setIsStudyGuide,
      setContent,
      setIsPreview,
      setContextPreview,
    ],
  );

  //Show Material Content
  const handleShowMaterial = useCallback(
    (content: Content, lesson: Lesson, printable?: boolean) => {
      setContent(content);
      setNeedPrint(printable ?? false);
      if (isStudyGuide) setIsStudyGuide(false);
      setShowInstructionModal(false);
      setShowLessonPreView(false);
      setContextPreview(false);
      setShowGuideSessionModal(false);
      setShowStudyGuidePreview(false);
      setShowTeachView(false);
      setIsPreview(true);
      setShowMaterial(true);
    },
    [
      setContent,
      isStudyGuide,
      setIsStudyGuide,
      setContextPreview,
      setIsPreview,
    ],
  );

  // const handleDownloadTeacherKey = useCallback(() => {
  //   if (isStudyGuide) setIsStudyGuide(false);
  //   setShowInstructionModal(false);
  //   setShowLessonPreView(false);
  //   setShowGuideSessionModal(false);
  //   setShowStudyGuidePreview(false);
  //   setShowTeachView(false);

  //   if (lesson && lesson.materials && lesson.materials.length > 0) {
  //     const teacherKey = lesson?.materials?.find(m => m?.name.toLowerCase() === 'teacher key');
  //     if (teacherKey) {
  //       handleShowMaterial(teacherKey, lesson);
  //     } else {
  //       setShowMaterialView(true)
  //     }
  //   } else {
  //     handleOpenTeacher();
  //   }
  // }, [
  //   lesson,
  //   setShowLessonPreView,
  //   isStudyGuide,
  //   setIsStudyGuide,
  //   handleShowMaterial,
  //   handleOpenTeacher,
  // ]);

  const handleStartLessonWithLonger = useCallback(() => {
    setSection(undefined);
    setLessonRunModal(false);
    setIsViewExitTicket(false);
    if (unitId === WellBeingUnitId) {
      trackEvent(Events.actions.educatorWellbeingUnitLessonOpen, {
        course_name: program?.name || currentProgram?.name,
        unit_name: unitData?.name,
        lesson_name: lesson?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
    if (lesson?.isAlternative && lesson?.id) {
      switchLessonFlow({
        variables: {
          input: {
            unitId: unitId,
            lessonId: lesson?.id,
            isAlternative: false,
          },
        },
      });
    }
    if (
      lesson?.materials &&
      lesson?.materials.length > 0 &&
      !showInstructionModal
    ) {
      setShowTeachView(false);
      setShowInstructionModal(true);
      return;
    }
    setTimeout(() => {
      if (lesson?.modules) {
        if (isHomeEDApp) {
          setContent(selectFirstContentExceptSurvey(lesson.modules));
        } else {
          setContent(lesson.modules.at(0) as any);
        }
        setLastPageNum(1);
      }
      setShowLessonView(true);
      trackEvent(Events.actions.lessonOpened, {
        course_name: program?.name || currentProgram?.name,
        unit_name: unitData?.name,
        lesson_name: lesson?.name,
        lesson_number: lesson?.number,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }, 200);
  }, [
    lesson,
    setSection,
    setIsViewExitTicket,
    unitId,
    unitData?.name,
    showInstructionModal,
    trackEvent,
    program?.name,
    currentProgram?.name,
    switchLessonFlow,
    setContent,
    user,
  ]);

  const handleStartLessonWithMulti = useCallback(
    (lesson?: Lesson, isFromPreview?: boolean) => {
      // Start Alternate Lesson from Preview
      setIsFromPreview(!!isFromPreview);
      if (lesson) {
        setLesson(lesson);
        setIsPreview(false);
        setShowLessonPreView(false);
      }
      //
      setLessonRunModal(false);
      setAlternateLessonRunModal(true);
    },
    [setIsPreview, setLesson, setAlternateLessonRunModal],
  );

  const handleStartAlternateLesson = useCallback(() => {
    setLessonRunModal(false);
    setAlternateLessonRunModal(false);
    setIsViewExitTicket(false);
    setShowExerciseModal(true);
  }, [setIsViewExitTicket, setShowExerciseModal]);

  const handleStartExercise = useCallback(
    (exercise: SectionFragment, isFromPreview?: boolean) => {
      setSection(exercise);
      setIsFromPreview(!!isFromPreview);
      if (!lesson?.isAlternative && lesson?.id) {
        switchLessonFlow({
          variables: {
            input: {
              unitId: unitId,
              lessonId: lesson?.id,
              isAlternative: true,
            },
          },
        });
      }
      setStartedAlternateLesson(true);
      trackEvent(Events.actions.sectionOpened, {
        course_name: program?.name || currentProgram?.name,
        unit_name: unitData?.name,
        lesson_name: lesson?.name,
        lesson_number: lesson?.number,
        section_number: section?.position,
        section_name: section?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
      if (exercise.modules) {
        setContent(exercise.modules.at(0) as any);
        setLastPageNum(1);
      }
      if (showLessonPreView) {
        setShowLessonPreView(false);
      }
      setShowExerciseModal(false);
      setShowLessonView(true);
    },
    [
      setSection,
      lesson?.isAlternative,
      lesson?.id,
      lesson?.name,
      lesson?.number,
      setStartedAlternateLesson,
      trackEvent,
      program?.name,
      currentProgram?.name,
      unitData?.name,
      section?.position,
      section?.name,
      showLessonPreView,
      setShowExerciseModal,
      switchLessonFlow,
      unitId,
      setContent,
      user,
    ],
  );

  const handleStartIntroduction = useCallback(
    (exercise: SectionFragment) => {
      setSection(exercise);
      if (!lesson?.isAlternative && lesson?.id) {
        switchLessonFlow({
          variables: {
            input: {
              unitId: unitId,
              lessonId: lesson?.id,
              isAlternative: true,
            },
          },
        });
      }
      setStartedAlternateLesson(true);
      trackEvent(Events.actions.sectionOpened, {
        course_name: program?.name || currentProgram?.name,
        unit_name: unitData?.name,
        lesson_name: lesson?.name,
        lesson_number: lesson?.number,
        section_number: section?.position,
        section_name: section?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
      if (exercise.modules) {
        setContent(exercise.modules.at(0) as any);

        setLastPageNum(1);
      }
      setShowExerciseModal(false);
      setShowLessonView(true);
    },
    [
      setSection,
      lesson?.isAlternative,
      lesson?.id,
      lesson?.name,
      lesson?.number,
      setStartedAlternateLesson,
      trackEvent,
      program?.name,
      currentProgram?.name,
      unitData?.name,
      section?.position,
      section?.name,
      setShowExerciseModal,
      switchLessonFlow,
      unitId,
      setContent,
      user,
    ],
  );

  const handleCloseExerciseModal = () => {
    setSection(undefined);
    setShowExerciseModal(false);
    setStartedAlternateLesson(false);

    document.getElementById("modal-body")!.style.overflow = "auto";
    if (!isParentApp && !isHomeEDApp && unitId) {
      getLasVisitedLessonByProgramId({
        variables: {
          input: { id: unitId },
        },
      });
    }
    if (unitId) {
      if (isHomeEDApp) {
        heaGetUnitInfo({
          variables: { input: { id: unitId } },
        });
        if (studyPlan?.id) {
          getHeaLessonCompletedStatus();
        }
      } else {
        getUnitInfo({
          variables: { input: { id: unitId } },
        });
      }
    }

    setShowLessonView(false);
  };

  const [heaUnlockAccount, { loading: unlocking }] =
    useHeaUnlockAccountMutation({
      onCompleted: (res) => {
        if (res.heaUnlockAccount) {
          setLocked(false);
          setAccountLocked(false);
          setShowPasscodeBox(false);
          setPasscode("");
          onClose();
        } else {
          setServerError("Unlock failed. Please try again");
        }
      },
      onError: (e) => {
        setServerError(e.message);
      },
    });

  const unlockAccount = useCallback(() => {
    heaUnlockAccount({
      variables: { input: { passcode } },
    });
  }, [heaUnlockAccount, passcode]);

  const unlockWithPassword = useCallback(
    async (password: string) => {
      const res = await heaUnlockAccount({
        variables: {
          input: { passcode: password },
        },
      });
      if (res.data?.heaUnlockAccount) {
        setParentFirstLogin("YES");
        onClose();
      }
    },
    [heaUnlockAccount, setParentFirstLogin, onClose],
  );

  const handleCloseLesson = useCallback(() => {
    if (section && startedAlternateLesson) {
      setSection(undefined);
      setShowLessonView(false);
      if (
        section.name === "Introduction" &&
        lesson?.sections &&
        isFromPreview
      ) {
        const currentSectionIndex = (lesson?.sections || []).findIndex(
          (sec) => sec.id === section.id,
        );
        let nextSection: SectionFragment | undefined;
        if (
          currentSectionIndex === -1 ||
          currentSectionIndex === lesson?.sections?.length - 1
        ) {
          nextSection = undefined;
        } else {
          nextSection = (lesson?.sections || []).find(
            (sec, index) =>
              sec.id && index > currentSectionIndex && !!sec.modules?.length,
          );
        }
        if (nextSection) {
          handleStartExercise(nextSection);
          return;
        } else {
          setShowExerciseModal(true);
        }
      } else {
        setIsViewExitTicket(false);
        setShowExerciseModal(true);
      }
      return;
    }

    document.getElementById("modal-body")!.style.overflow = "auto";
    if (!isParentApp && !isHomeEDApp && curExtendedUnit?.id) {
      getLasVisitedLessonByProgramId({
        variables: {
          input: { id: curExtendedUnit.id },
        },
      });
    }
    if (!isParentApp && !isHomeEDApp && curExtendedUnit?.id) {
      getLastVisitedUnitStudyReview({
        variables: {
          input: { id: curExtendedUnit.id },
        },
      });
    }
    if (curExtendedUnit?.id) {
      if (isHomeEDApp) {
        heaGetUnitInfo({
          variables: { input: { id: curExtendedUnit?.id ?? unitId } },
        });
        if (studyPlan?.id) {
          getHeaLessonCompletedStatus();
        }
      } else {
        getUnitInfo({
          variables: { input: { id: curExtendedUnit?.id ?? unitId } },
        });
      }
    }
    // if (isHomeEDApp && accountLocked) {
    //   finishStudyPlan();
    // }
    setGetBack(true);
    setShowLessonView(false);
    setCompleteModal(false);
    setStartResume(false);
    setShowMaterial(false);
    setContextPreview(false);
  }, [
    section,
    startedAlternateLesson,
    isParentApp,
    unitId,
    setSection,
    isFromPreview,
    lesson?.sections,
    handleStartExercise,
    setShowExerciseModal,
    setIsViewExitTicket,
    getLasVisitedLessonByProgramId,
    heaGetUnitInfo,
    studyPlan?.id,
    getHeaLessonCompletedStatus,
    getUnitInfo,
    curExtendedUnit?.id,
    setCompleteModal,
    getLastVisitedUnitStudyReview,
    setContextPreview,
  ]);

  // Start Lesson from Begin on Preview
  const handleStartLessons = useCallback(
    (lesson: Lesson) => {
      setLesson(lesson);
      if (
        lesson.materials &&
        lesson.materials.length > 0 &&
        !showInstructionModal
      ) {
        setShowTeachView(false);
        setShowInstructionModal(true);
        return;
      }
      setShowInstructionModal(false);
      if (lesson.modules) {
        if (isHomeEDApp) {
          setContent(selectFirstContentExceptSurvey(lesson.modules));
        } else {
          setContent(lesson.modules.at(0) as any);
        }
        setLastPageNum(1);
      }
      setIsPreview(false);
      setShowLessonPreView(false);
      setShowLessonView(true);
      trackEvent(Events.actions.lessonOpened, {
        course_name: program?.name || currentProgram?.name,
        unit_name: curUnitContent?.name,
        lesson_name: lesson?.name,
        lesson_number: lesson?.number,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
      if (unitId === WellBeingUnitId) {
        trackEvent(Events.actions.educatorWellbeingUnitLessonOpen, {
          course_name: program?.name || currentProgram?.name,
          unit_name: curUnitContent?.name,
          lesson_name: lesson?.name,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      }
    },
    [
      program?.name,
      currentProgram?.name,
      setContent,
      setLesson,
      setIsPreview,
      trackEvent,
      curUnitContent,
      unitId,
      showInstructionModal,
      user,
    ],
  );

  // Start Lesson from Requirement modal
  const handleStartLesson = useCallback(() => {
    videoPlayerRef.current?.setMute(true);
    setIsPreview(false);
    setShowLessonPreView(false);
    setShowInstructionModal(false);

    if (lesson) {
      setTimeout(() => {
        if (
          lesson.sections &&
          lesson.sections.length > 0 &&
          lesson.alternativeActivated
        ) {
          if (lesson?.modules) {
            if (isHomeEDApp) {
              setContent(selectFirstContentExceptSurvey(lesson.modules));
            } else {
              setContent(lesson.modules.at(0) as any);
            }
            setLastPageNum(1);
          }
          if (newLessonLayout) {
            setShowLessonPreView(true);
            setContextPreview(true);
          } else {
            setShowLessonView(true);
          }
          trackEvent(Events.actions.lessonOpened, {
            course_name: program?.name || currentProgram?.name,
            unit_name: unitData?.name,
            lesson_name: lesson?.name,
            lesson_number: lesson?.number,
            schoolId: user?.schools?.[0]?.id,
            schoolName: user?.schools?.[0]?.name,
            userId: user?.id,
            userName: `${user?.firstName} ${user?.lastName}`,
          });
        } else {
          const index =
            lesson.modules?.findIndex(
              (m) =>
                m?.id === LasVisitedLesson?.lastVisitedLesson?.[0]?.moduleId,
            ) ?? -1;
          if (index > -1 && startResume) {
            setContent(lesson.modules?.at(index) as any);
            setLastPageNum(
              LasVisitedLesson?.lastVisitedLesson?.[0]?.pageNumber ?? 1,
            );

            setShowLessonView(true);
            trackEvent(Events.actions.lessonOpened, {
              course_name: program?.name || currentProgram?.name,
              unit_name: unitData?.name,
              lesson_name: lesson?.name,
              lesson_number: lesson?.number,
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} ${user?.lastName}`,
            });
          } else {
            if (lesson.modules) {
              if (isHomeEDApp) {
                setContent(selectFirstContentExceptSurvey(lesson.modules));
              } else {
                setContent(lesson.modules.at(0) as any);
              }
              setLastPageNum(1);
            }
            if (newLessonLayout) {
              setShowLessonPreView(true);
              setContextPreview(true);
            } else {
              setShowLessonView(true);
            }
            trackEvent(Events.actions.lessonOpened, {
              course_name: program?.name || currentProgram?.name,
              unit_name: unitData?.name,
              lesson_name: lesson?.name,
              lesson_number: lesson?.number,
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} ${user?.lastName}`,
            });
          }
        }
      }, 200);
    }
  }, [
    setIsPreview,
    lesson,
    newLessonLayout,
    trackEvent,
    program?.name,
    currentProgram?.name,
    unitData?.name,
    user?.schools,
    user?.id,
    user?.firstName,
    user?.lastName,
    setContent,
    setContextPreview,
    startResume,
    LasVisitedLesson?.lastVisitedLesson,
  ]);

  const handleLessonPreview = useCallback(
    (lesson: Lesson, parentSkill?: string) => {
      setTimeout(() => {
        setIsPreview(true);
        setShowLessonPreView(true);
        setContextPreview(true);
        setLesson(lesson);
        setIsViewExitTicket(false);
        videoPlayerRef.current?.setMute(true);

        if (lesson.modules) {
          if (isStudyGuide) setIsStudyGuide(false);
          if (parentSkill) {
            const parentSkillContent = lesson.modules.find(
              (m) => m.id === parentSkill,
            );
            if (parentSkillContent) {
              setContent(parentSkillContent);
            } else {
              if (isHomeEDApp) {
                setContent(selectFirstContentExceptSurvey(lesson.modules));
              } else {
                setContent(lesson.modules.at(0) as any);
              }
            }
          } else {
            if (isHomeEDApp) {
              setContent(selectFirstContentExceptSurvey(lesson.modules));
            } else {
              setContent(lesson.modules.at(0) as any);
            }
          }
          setLastPageNum(1);
        }
      }, 200);
    },
    [
      setIsPreview,
      setContextPreview,
      setLesson,
      setIsViewExitTicket,
      isStudyGuide,
      setIsStudyGuide,
      setContent,
    ],
  );

  const handleTeacherFeedback = useCallback(
    (lesson: Lesson) => {
      videoPlayerRef.current?.setMute(true);
      setTimeout(() => {
        setLesson(lesson);
        setShowLessonFeedback(true);
      }, 200);
    },
    [setLesson],
  );

  const handleShowConfirmPasswordModal = (lesson: Lesson) => {
    setLesson(lesson);
    setShowConfirmCompleteModal(true);
  };

  const handleShowApprovalPendingModal = () => {
    setConfirmPWDForPendingLessons(true);
    setShowConfirmCompleteModal(true);
  };

  const handleClosePendingModal = () => {
    setShowPendingLessons(false);
    getHeaLessonCompletedStatus();
  };

  const handleConfirmPassword = () => {
    setShowConfirmCompleteModal(false);
    if (!locked) {
      setConfirmedPassword(true);
    }
    if (lessonConfirm) {
      setLessonConfirm(false);
      markLessonAsConfirmWith(true);
    }
    if (reviewLesson) {
      setReviewLesson(false);
      setShowReview(true);
    }
    if (isViewExitTicket) {
      setShowLessonView(true);
    }

    if (confirmPWDForPendingLessons) {
      setConfirmPWDForPendingLessons(false);
      // setShowPendingLessons(true);
      unlockWithPassword(password ?? "");
    }
  };

  const handleClosePasswordModal = () => {
    setShowConfirmCompleteModal(false);
    setConfirmedPassword(false);
    setLessonConfirm(false);
    setReviewLesson(false);
    setIsViewExitTicket(false);
    setShowPendingLessons(false);
    setConfirmPWDForPendingLessons(false);
  };

  const handleCloseLessonFeedback = useCallback(() => {
    setShowLessonFeedback(false);
    document.getElementById("modal-body")!.style.overflow = "auto";
  }, []);

  const handleCloseLessonPreview = useCallback(() => {
    setIsPreview(false);
    setShowLessonPreView(false);
    setContextPreview(false);

    if (lessonInfoByUniqueName) {
      const lessonUniqueName = pathname.split("/")[2];
      history.replace(`${pathname.replace(`/${lessonUniqueName}`, "")}`);
    }
    document.getElementById("modal-body")!.style.overflow = "auto";
    if (newLessonLayout) {
      handleCloseLesson();
    }
  }, [
    setIsPreview,
    pathname,
    lessonInfoByUniqueName,
    history,
    newLessonLayout,
    handleCloseLesson,
    setContextPreview,
  ]);

  const handleCloseResumeModal = useCallback(() => {
    setResumeModal(false);
    document.getElementById("modal-body")!.style.overflow = "auto";
    if (isStudyGuide) {
      setIsStudyGuide(false);
    }
  }, [isStudyGuide, setIsStudyGuide]);

  const handleGoLessons = useCallback(() => {
    videoPlayerRef.current?.setMute(true);
    const lessonForm = document.getElementById("lessonform");
    const body = document.getElementById("modal-body");
    if (lessonForm && body) {
      let dims = lessonForm.getBoundingClientRect();

      var elementPosition = dims.top;
      var offsetPosition = elementPosition + body.scrollTop - 160;

      document.getElementById("modal-body")?.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    } else {
      lessonForm?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const handleViewUnit = useCallback(() => {
    setQuizUnit(false);
  }, []);

  const handleGoLesson = useCallback((lessonId: string) => {
    const lessonForm = document.getElementById(lessonId);
    const body = document.getElementById("modal-body");
    if (lessonForm && body) {
      let dims = lessonForm.getBoundingClientRect();

      var elementPosition = dims.top;
      var offsetPosition = elementPosition + body.scrollTop - 140;

      document.getElementById("modal-body")?.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    } else {
      lessonForm?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, []);

  const handleMoveLessons = useCallback(() => {
    setShowTeachView(false);
    setShowLessonPreView(false);
    setContextPreview(false);
    setTimeout(() => {
      handleGoLessons();
    }, 300);
  }, [handleGoLessons, setContextPreview]);

  const handleResumeLesson = useCallback(() => {
    if (LasVisitedLesson?.lastVisitedLesson && lesson?.modules) {
      const index = lesson.modules.findIndex(
        (m) => m?.id === LasVisitedLesson.lastVisitedLesson?.[0]?.moduleId,
      );
      if (index > -1) {
        setContent(lesson.modules.at(index) as any);
        setLastPageNum(
          LasVisitedLesson.lastVisitedLesson?.[0]?.pageNumber ?? 1,
        );
      } else {
        if (isHomeEDApp) {
          setContent(selectFirstContentExceptSurvey(lesson.modules));
        } else {
          setContent(lesson.modules.at(0) as any);
        }
        setLastPageNum(1);
      }
      setShowLessonPreView(false);
      setContextPreview(false);
      setResumeModal(false);
      setIsPreview(false);
      setShowStudyGuidePreview(false);
      setShowLessonView(true);
      trackEvent(
        Events.actions.resumeButtonPressed,
        {
          course_name: program?.name || currentProgram?.name,
          unit_name: unitData?.name,
          lesson_name: lesson?.name,
          lesson_number: lesson?.number,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        },
        true,
      );
    } else if (
      LasVisitedStudyGuide?.lastVisitedUnitStudyReview &&
      studyGuides &&
      isStudyGuide
    ) {
      const index = studyGuides.findIndex(
        (m) =>
          m?.id ===
          LasVisitedStudyGuide?.lastVisitedUnitStudyReview?.[0]?.moduleId,
      );
      if (index > -1) {
        setContent(studyGuides.at(index) as any);
        setLastPageNum(
          LasVisitedStudyGuide?.lastVisitedUnitStudyReview?.[0]?.pageNumber ??
            1,
        );
      } else {
        if (isHomeEDApp) {
          setContent(selectFirstContentExceptSurvey(studyGuides));
        } else {
          setContent(studyGuides.at(0) as any);
        }
        setLastPageNum(1);
      }
      setShowStudyGuidePreview(false);
      setResumeModal(false);
      setIsPreview(false);
      setShowLessonView(true);
      trackEvent(
        Events.actions.resumeButtonPressed,
        {
          course_name: program?.name || currentProgram?.name,
          unit_name: unitData?.name,
          lesson_name: lesson?.name,
          lesson_number: lesson?.number,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        },
        true,
      );
    }
  }, [
    LasVisitedLesson?.lastVisitedLesson,
    lesson?.modules,
    lesson?.name,
    lesson?.number,
    setIsPreview,
    trackEvent,
    program?.name,
    currentProgram?.name,
    unitData?.name,
    setContent,
    studyGuides,
    LasVisitedStudyGuide?.lastVisitedUnitStudyReview,
    isStudyGuide,
    user,
    setContextPreview,
  ]);

  const handleStartBegin = useCallback(() => {
    if (lesson?.modules) {
      if (isHomeEDApp) {
        setContent(selectFirstContentExceptSurvey(lesson.modules));
      } else {
        setContent(lesson.modules.at(0) as any);
      }
      setLastPageNum(1);
    } else if (studyGuides.length > 0 && isStudyGuide) {
      setContent(studyGuides.at(0) as any);
      setLastPageNum(1);
    }
    setShowStudyGuidePreview(false);
    setShowLessonPreView(false);
    setContextPreview(false);
    setResumeModal(false);
    setIsPreview(false);
    setShowLessonView(true);
    trackEvent(Events.actions.lessonOpened, {
      course_name: program?.name || currentProgram?.name,
      unit_name: unitData?.name,
      lesson_name: lesson?.name,
      lesson_number: lesson?.number,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [
    lesson?.modules,
    lesson?.name,
    setIsPreview,
    trackEvent,
    program?.name,
    currentProgram?.name,
    unitData?.name,
    setContent,
    lesson?.number,
    studyGuides,
    isStudyGuide,
    user,
    setContextPreview,
  ]);

  const handleOpenExitTicket = useCallback(
    (lesson: Lesson, studyPlan: StudyPlanContentFragment) => {
      setLesson(lesson);
      setCurrentStudyplan(studyPlan);

      if (lesson.modules) {
        const exitTicketForm = lesson.modules.find(
          (c) => c.type === ContentType.FORM,
        );
        const exitTicketSlide = lesson.modules.find((c) =>
          c.name.toLowerCase().includes("exit ticket"),
        );
        if (exitTicketForm) {
          setContent(exitTicketForm);
        } else if (exitTicketSlide) {
          setContent(exitTicketSlide);
        }
        setLastPageNum(1);
      }
      setIsViewExitTicket(true);

      if (locked) {
        setShowConfirmCompleteModal(true);
      } else {
        setShowLessonView(true);
      }
    },
    [locked, setCurrentStudyplan, setLesson, setContent, setIsViewExitTicket],
  );

  const markLessonAsConfirmWith = useCallback(
    async (complete: boolean) => {
      if (!studyPlan?.id || !lesson) {
        return;
      }
      const res = await heaConfirmLesson({
        variables: {
          input: {
            studyplanId: studyPlan.id,
            lessonId: lesson.id,
            completed: complete,
          },
        },
      });
      if (res?.data?.heaConfirmLesson) {
        getHeaLessonCompletedStatus();
      }
    },
    [lesson, studyPlan?.id, getHeaLessonCompletedStatus, heaConfirmLesson],
  );

  const handleConfirmLesson = (
    lesson: Lesson,
    studyPlan: StudyPlanContentFragment,
  ) => {
    setLesson(lesson);
    setCurrentStudyplan(studyPlan);
    setLessonConfirm(true);
    if (locked) {
      setShowConfirmCompleteModal(true);
    } else {
      markLessonAsConfirmWith(true);
    }
  };

  const handleReviewLesson = (
    lesson: Lesson,
    studyPlan: StudyPlanContentFragment,
  ) => {
    setLesson(lesson);
    setCurrentStudyplan(studyPlan);
    setIsViewExitTicket(false);
    setReviewLesson(true);
    if (locked) {
      setShowConfirmCompleteModal(true);
    } else {
      setShowReview(true);
    }
  };

  const handleReviewNow = useCallback(
    (lesson: Lesson) => {
      setLesson(lesson);
      setIsViewExitTicket(false);
      setShowReview(false);
      setShowLessonPreView(false);
      setContextPreview(false);
      if (locked) {
        if (lesson.modules) {
          if (isHomeEDApp) {
            setContent(selectFirstContentExceptSurvey(lesson.modules));
          } else {
            setContent(lesson.modules.at(0) as any);
          }
          setLastPageNum(1);
        }
        setShowLessonView(true);
      } else {
        setLessonRunModal(true);
      }

      markLessonAsConfirmWith(false);
    },
    [
      locked,
      markLessonAsConfirmWith,
      setIsViewExitTicket,
      setLesson,
      setContent,
      setLastPageNum,
      setShowReview,
      setShowLessonView,
      setLessonRunModal,
      setContextPreview,
    ],
  );

  const handleReviewLater = useCallback(
    (lesson: Lesson) => {
      setLesson(lesson);
      setIsViewExitTicket(false);
      setShowReview(false);

      markLessonAsConfirmWith(false);
    },
    [markLessonAsConfirmWith, setIsViewExitTicket, setLesson, setShowReview],
  );

  const handleCloseReviewModal = () => {
    setIsViewExitTicket(false);
    setShowReview(false);
  };

  const handleOpenUnitShareModal = (unit: any) => {
    setSharedObject(unit);
    videoPlayerRef.current?.setMute(true);
    setShareLesson(true);
  };

  const handleOpenShareModal = (lesson: any) => {
    setSharedObject(lesson);
    videoPlayerRef.current?.setMute(true);
    if (
      assessments ||
      isPostProgram ||
      unitId === WellBeingUnitId ||
      unitId === CreatingResilientAusClassroomsId ||
      unitId === CreatingResilientClassroomsId
    ) {
      setShareLesson(true);
    } else {
      setOpenShareModal(true);
    }
  };

  const handleCloseShareModal = () => {
    setSharedObject(undefined);
    setOpenShareModal(false);
    setShareLesson(false);
  };

  const handleShareTeacher = () => {
    setOpenShareModal(false);
    setShareLesson(true);
  };

  const handleShareStudent = () => {
    setOpenShareModal(false);
    setShowStudentLinkModal(true);
    const selExitTicket = exitTickets.find(
      (ticket) => ticket.lessonNum === sharedObject.number,
    );
    setFormData(selExitTicket?.formData ? selExitTicket?.formData : undefined);
  };

  const handleMaterialPreview = () => {
    const elementMenu = document.getElementById("lessonform");
    if (elementMenu) {
      elementMenu.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const tpreps = useMemo(
    () => [...(curUnitContent?.teacherPreparation || [])].filter(notEmpty),
    [curUnitContent?.teacherPreparation],
  );
  const ppreps = useMemo(
    () => [...(curExtendedUnit2?.parentPreparation || [])].filter(notEmpty),
    [curExtendedUnit2?.parentPreparation],
  );
  const cpres = useMemo(() => {
    const classPresentation = [
      ...(curExtendedUnit?.classPresentation || []),
    ].filter(notEmpty);
    classPresentation.sort(sortByNumber);
    if (isHomeEDApp && heaLessonsStatus?.heaGetLessonStatus?.length) {
      (heaLessonsStatus?.heaGetLessonStatus || []).forEach(
        (heaLessonStatus) => {
          const lessonId = classPresentation.findIndex(
            (lesson) => lesson.id === heaLessonStatus.lessonId,
          );
          if (lessonId > -1) {
            const newObject = { ...(classPresentation[lessonId] as Lesson) };
            newObject.status = heaLessonStatus.status;
            classPresentation[lessonId] = newObject;
          }
        },
      );
    }
    return classPresentation.map((lessonData) => ({
      ...lessonData,
      isOnlyNotCompletedLesson:
        (lessonData as Lesson).status !== LessonStatus.Completed &&
        classPresentation
          .filter((lesson) => lesson?.id !== lessonData.id)
          .every(
            (lesson) => (lesson as Lesson).status === LessonStatus.Completed,
          ),
    }));
  }, [
    curExtendedUnit?.classPresentation,
    heaLessonsStatus?.heaGetLessonStatus,
  ]);

  // const parentStudyPlan = useMemo(() => studyPlansForSelectedUnit?.find(plan => plan.studyplan.name.includes(parentPlanName)), [studyPlansForSelectedUnit])
  const filteredStudyPlans = useMemo(() => {
    var studyplans = [...(studyPlansForSelectedUnit || [])].filter(
      (plan) => !plan.name.includes(parentPlanName),
    );
    if (studyMode === ModeTypeEnum.Study) {
      studyplans = studyplans.filter((plan) =>
        plan.name.includes(studyPlan?.name || ""),
      );
    }
    return studyplans;
  }, [studyPlansForSelectedUnit, studyPlan?.name, studyMode]);

  useEffect(() => {
    if (isParentApp && parentSkill && !isHomeEDApp) {
      if (cpres?.length) {
        const parentAudioSeries = cpres.find(
          (audio) => audio?.name === "Parenting Audio Series",
        );
        if (parentAudioSeries) {
          handleLessonPreview(parentAudioSeries as LessonFragment, parentSkill);
        }
      }
    }
  }, [cpres, handleLessonPreview, isParentApp, parentSkill]);

  useEffect(() => {
    if (lessonInfoByUniqueName) {
      handleLessonPreview(
        lessonInfoByUniqueName as LessonFragment,
        parentSkill,
      );
    }
  }, [lessonInfoByUniqueName, handleLessonPreview, parentSkill]);

  const unitName = useMemo(
    () => curExtendedUnit?.name || "",
    [curExtendedUnit?.name],
  );
  const subHeading = useMemo(
    () => curExtendedUnit?.subHeading || "",
    [curExtendedUnit?.subHeading],
  );

  const onPlayPreviewVideo = useCallback(() => {
    trackEvent(Events.actions.playPreviewVideo, {
      unit_name: unitName,
      course_name: program?.name || currentProgram?.name,
      schoolId: user?.schools?.[0]?.id,
      schoolName: user?.schools?.[0]?.name,
      userId: user?.id,
      userName: `${user?.firstName} ${user?.lastName}`,
    });
  }, [program?.name, currentProgram?.name, trackEvent, unitName, user]);

  const videoPlayerRef = useRef<{ setMute: (muted: boolean) => void }>(null);

  const videoRender = useMemo(() => {
    return (
      <CoverVideoPlayer
        ref={videoPlayerRef}
        src={curExtendedUnit?.popupCover}
        playing={isInViewport}
        title={unitName}
        subTitle={subHeading}
        handleGoLessons={isQuizUnit ? handleViewUnit : handleGoLessons}
        metaData={{ video_title: "Cover Video" }}
        onPlayPreviewVideo={onPlayPreviewVideo}
        assessments={assessments}
        viewLabel={
          assessments
            ? "View Assessments"
            : isQuizUnit
              ? "View Unit"
              : "View Lessons"
        }
        unitUniqueName={curUnitContent?.uniqueName}
        isNewCover={curUnitContent?.isNewCover ?? false}
        suggestedDuration={curUnitContent?.suggestedDuration ?? ""}
        suggestedLessonCount={`${curUnitContent?.suggestedLessonCount}`}
        handleUnitShareModal={() => handleOpenUnitShareModal(curUnitContent)}
      />
    );
  }, [
    curExtendedUnit?.popupCover,
    isInViewport,
    unitName,
    subHeading,
    handleGoLessons,
    onPlayPreviewVideo,
    assessments,
    handleViewUnit,
    isQuizUnit,
    curUnitContent,
  ]);

  const lessonInfo = useMemo(
    () => ({
      unit_name: curExtendedUnit?.name || "",
      unit_id: curExtendedUnit?.id || "",
      course_name: program?.name || currentProgram?.name || "",
      course_id: program?.id || currentProgram?.id || "",
      lesson_name: lesson?.name || "",
      lesson_id: lesson?.id || "",
      lesson_position: lesson?.number || 0,
    }),
    [
      program?.id,
      program?.name,
      currentProgram?.id,
      currentProgram?.name,
      curExtendedUnit?.id,
      curExtendedUnit?.name,
      lesson?.id,
      lesson?.name,
      lesson?.number,
    ],
  );

  const completedPercent = useMemo(() => {
    const completed = (
      curExtendedUnit as ExtendedUnitFragment
    )?.classPresentation?.filter(
      (lesson) => lesson?.status === "completed",
    ).length;
    let percent = 0;
    if (curExtendedUnit?.classPresentation && completed) {
      percent = (completed / curExtendedUnit.classPresentation.length) * 100;
    }
    return percent;
  }, [curExtendedUnit]);

  const onBackMouseOver = useCallback(() => {
    setBackHover(true);
  }, []);

  const onBackMouseLeave = useCallback(() => {
    setBackHover(false);
  }, []);

  const handleChange = useCallback(
    (setter) => (value: TextInputValue) => {
      setter(value);
      setServerError("");
    },
    [],
  );

  const moveNext = useCallback(() => {
    setShowPasscodeBox(true);
    setPasscode("");
  }, []);

  const movePrev = useCallback(() => {
    setShowPasscodeBox(false);
    setPasscode("");
    setServerError("");
  }, []);

  const handleActiveFormPreview = useCallback(
    (feedback: FeedbackFormContent, expiration: any, ticketCount?: any) => {
      const formId = getFormId(lesson?.id);
      if (formId) {
        // if exist ExitTicket link, cancel old one
        cancelFeedbackForm({
          variables: {
            input: { id: formId },
          },
        });
      }
      const url = createURL(feedback.link ?? "");
      window.navigator.clipboard.writeText(url);
      successToast(
        "Exit Ticket Link Successfully Created and Link copied successfully!",
      );
      setFormId(lesson!.id, feedback.id);
      const tickets = exitTickets.map((ticket) => {
        if (lesson?.number === ticket.lessonNum) {
          return {
            ...ticket,
            status: true,
            exitTicketCount: ticketCount,
            expiryMinutes: expiration * 60,
            copyLink: url,
            code: feedback.link ?? "",
          };
        }
        return ticket;
      });
      setExitTickets(tickets);
    },
    [
      lesson,
      exitTickets,
      setExitTickets,
      successToast,
      getFormId,
      setFormId,
      cancelFeedbackForm,
    ],
  );

  const handleGenerateTicket = useCallback(
    (lesson: Lesson) => {
      setLesson(lesson);
      setShowExitModal(true);
      const selExitTicket = exitTickets.find(
        (ticket) => ticket.lessonNum === lesson.number,
      );
      setFormData(
        selExitTicket?.formData ? selExitTicket?.formData : undefined,
      );
    },
    [setLesson, setShowExitModal, exitTickets],
  );

  const handleCancelExitTicket = useCallback(
    (lesson: Lesson) => {
      removeFormId(lesson?.id);
      const tickets = exitTickets.map((ticket) => {
        if (lesson?.number === ticket.lessonNum) {
          return {
            ...ticket,
            status: false,
            exitTicketCount: 0,
            expiryMinutes: 0,
            copyLink: "",
          };
        }
        return ticket;
      });
      setExitTickets(tickets);
    },
    [exitTickets, setExitTickets, removeFormId],
  );

  const handleCopyToClipboard = useCallback(async () => {
    successToast("Link Copied Successfully!");
    setShareLesson(false);
    setSharedObject(undefined);
  }, [successToast]);

  const handleActiveStudentLink = useCallback(
    async (feedback: AbsentStudentLesson) => {
      const url = createStudentLink(feedback.link ?? "");
      await window.navigator.clipboard.writeText(url);
      successToast(
        "Link Created Successfully. Please share this link with your student(s).",
      );
    },
    [successToast],
  );

  return (
    <UnitModal
      open={open}
      onClose={handleClose}
      onScroll={handleScroll}
      closeButton={isHomeEDApp ? !locked : true}
      isLoading={
        (isHomeEDApp ? loading2 : loading) || visitloading || studyReviewLoading
      }
    >
      {(isHomeEDApp ? loading2 : loading) ||
      visitloading ||
      loading3 ||
      loading4 ||
      studyReviewLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <div
          className={styles.wrapper}
          style={{
            paddingTop: isHomeEDApp ? "62px" : "90px",
            position:
              showTeachView ||
              showLessonView ||
              showLessonPreView ||
              showLessonFeedback ||
              showStudyGuidePreview ||
              showMaterail
                ? "unset"
                : "relative",
          }}
        >
          <div
            className={styles.backdrop}
            onClick={
              isHomeEDApp ? (!locked ? handleClose : () => {}) : handleClose
            }
          />
          {isHomeEDApp && locked && !showPasscodeBox ? (
            <div className={styles.studyplanSection}>
              <div className={styles.studyplanAvatar}>
                <div className={styles.studyplanAvatarTitle}>
                  {studyplanName.substring(0, 2).toLocaleUpperCase()}
                </div>
              </div>
              <div
                className={styles.studyplanSectionTitle}
              >{`${studyplanName}'s Study Plan`}</div>
              <div className={styles.studyplanSectionButtons}>
                <div className={styles.activebutton}>
                  <span>Active</span>
                  <div className={styles.pulseDotContainer}>
                    <div className={styles.dotBack} />
                    <div className={styles.dotFront} />
                  </div>
                </div>
                <div className={styles.endbutton} onClick={moveNext}>
                  <CloseIcon
                    className={styles.studyplanSectionButtonLeftIcon}
                  />
                  <span>End Study</span>
                </div>
              </div>
            </div>
          ) : null}
          {isHomeEDApp &&
          studyPlan &&
          studyMode === ModeTypeEnum.Study &&
          !locked ? (
            <div className={styles.topStudyPlanSection}>
              <div
                className={styles.studyplanAvatar}
                style={{ background: `${studyPlan.darkColor}` }}
              >
                <div className={styles.studyplanAvatarTitle}>
                  {studyPlan.name.substring(0, 2).toLocaleUpperCase()}
                </div>
              </div>
              <div
                className={styles.studyplanSectionTitle}
              >{`${studyPlan.name}'s Study Plan`}</div>
            </div>
          ) : null}
          {isHomeEDApp && locked && showPasscodeBox ? (
            <div className={styles.studyplanSection}>
              <div className={styles.backButtonDiv}>
                <Button
                  label=""
                  className={styles.backButton}
                  onClick={movePrev}
                  leftIcon={
                    <FSBackIcon
                      width={18}
                      height={18}
                      className={styles.studyplanSectionBackButton}
                    />
                  }
                />
              </div>
              <div className={styles.lockMessageDiv}>
                <div className={styles.messateTopDiv}>
                  <LockIcon className={styles.lockIcon} />
                  <span className={styles.lockMessage}>Account Locked!</span>
                </div>
                <span className={styles.passcodeMessage}>
                  Please enter your passcode or password
                </span>
              </div>
              <div className={styles.passcodeBoxDiv}>
                <TextInput
                  allowSpecialCharacters
                  type={"password"}
                  className={styles.input}
                  placeholder={""}
                  hasError={!!serverError}
                  subTitle={serverError}
                  label={""}
                  id={"confirmPassword"}
                  value={passcode}
                  onChange={handleChange(setPasscode)}
                />
              </div>
              <Button
                label="Unlock"
                color="orange"
                className={
                  passcode.length < 4 ? styles.okButton : styles.okEnableButton
                }
                onClick={unlockAccount}
                disabled={!passcode.length || unlocking}
                leftIcon={
                  unlocking ? <Spinner width={20} height={20} /> : undefined
                }
              />
            </div>
          ) : null}
          <div
            id="coverView"
            className={styles.coverView}
            style={{ marginTop: isHomeEDApp && studyPlan ? "34px" : "0" }}
          >
            {curExtendedUnit?.coverImage ? (
              <>{videoRender}</>
            ) : (
              <div
                className={styles.image}
                style={{
                  backgroundImage: `url(${
                    curExtendedUnit?.coverImage
                      ? getMediaAbsolutePath(curExtendedUnit.coverImage)
                      : ""
                  })`,
                }}
              >
                <div className={styles.unitTitle}>
                  <div className={styles.textView}>
                    <h2>{curExtendedUnit?.name}</h2>
                    <p>{curExtendedUnit?.subHeading}</p>
                  </div>
                  <Button
                    size="small"
                    label={
                      assessments
                        ? "View Assessments"
                        : quizUnit
                          ? "View Unit"
                          : "View Lessons"
                    }
                    className={styles.lessonViweButton}
                    onClick={handleGoLessons}
                  />
                </div>
              </div>
            )}
          </div>
          {isHomeEDApp ||
          assessments ||
          isQuizUnit ||
          isGroupOnboarding ? null : (
            <>
              <div className={styles.progressView}>
                <div className={styles.progressHead}>
                  <h2>Progress</h2>
                  {completedPercent === 0 && (
                    <span>You have not started any Lessons in this Unit.</span>
                  )}
                </div>
                <div className={styles.progressBackBar}>
                  <div
                    className={styles.progressBar}
                    style={{
                      width: `${completedPercent}%`,
                      borderRadius: completedPercent === 100 ? "3px" : "",
                    }}
                  ></div>
                  <div className={styles.iconView}>
                    {cpres.map((item: any, index: number) => {
                      const completed =
                        ((index + 1) / cpres.length) * 100 <= completedPercent;
                      return (
                        <div
                          key={`${item.id}_${index}`}
                          className={styles.lessonProgress}
                        >
                          {completed ? (
                            <CompleteIcon
                              style={{ cursor: "pointer" }}
                              onClick={() => handleGoLesson(item.id)}
                            />
                          ) : (
                            <BookMarkIcon
                              style={{ cursor: "pointer" }}
                              onClick={() => handleGoLesson(item.id)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className={styles.actionsView}>
                <Button
                  label={"View Lessons"}
                  size="small"
                  className={styles.viewLessons}
                  onClick={handleGoLessons}
                />
                <Button
                  label="Share Unit"
                  size="small"
                  className={styles.shareButton}
                  leftIcon={<ShareIcon width={24} height={24} />}
                  onClick={() => handleOpenUnitShareModal(curUnitContent)}
                />
              </div>
            </>
          )}
          {!((isHomeEDApp && locked) || assessments || isQuizUnit) && (
            <div className={styles.overview}>
              <div className={styles.overviewSubView}>
                <div className={styles.subTitle}>Overview</div>
                <OverviewForm
                  isGroupOnboarding={isGroupOnboarding}
                  unit={isHomeEDApp ? curUnitContent2 : curUnitContent}
                />
              </div>
            </div>
          )}
          {tpreps.length && !isQuizUnit ? (
            <div
              id="teachform"
              className={
                showTeachView ? styles.teacherFSView : styles.teacherView
              }
              style={{
                paddingLeft: showTeachView ? "0px" : "24px",
                paddingRight: showTeachView ? "0px" : "24px",
              }}
            >
              {showTeachView && (
                <div className={styles.fsBackdrop} onClick={handleClose} />
              )}
              <div
                className={
                  showTeachView
                    ? styles.teacherFSSubView
                    : styles.teacherSubView
                }
              >
                {showTeachView ? (
                  <div>
                    {curExtendedUnit ? (
                      <TeacherDetailView
                        unit={curExtendedUnit}
                        onClose={handleCloseTeacher}
                        goLessons={handleMoveLessons}
                      />
                    ) : null}
                  </div>
                ) : !isHomeEDApp && curUnitContent ? (
                  <TeacherForm
                    unit={curUnitContent}
                    openDetails={handleOpenTeacher}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
          {ppreps.length && !locked && !isQuizUnit ? (
            <div
              id="teachform"
              className={
                showTeachView ? styles.teacherFSView : styles.teacherView
              }
              style={{
                paddingLeft: showTeachView ? "0px" : "24px",
                paddingRight: showTeachView ? "0px" : "24px",
              }}
            >
              {showTeachView && (
                <div className={styles.fsBackdrop} onClick={handleClose} />
              )}
              <div
                className={
                  showTeachView
                    ? styles.teacherFSSubView
                    : styles.teacherSubView
                }
              >
                {showTeachView ? (
                  <div>
                    {curExtendedUnit ? (
                      <TeacherDetailView
                        unit={curExtendedUnit}
                        onClose={handleCloseTeacher}
                        goLessons={handleMoveLessons}
                      />
                    ) : null}
                  </div>
                ) : isHomeEDApp && curUnitContent2 ? (
                  <TeacherForm
                    unit={curUnitContent2}
                    openDetails={handlerOpenParentOverview}
                  />
                ) : null}
              </div>
            </div>
          ) : null}
          {!newLessonLayout ? (
            !isQuizUnit && (
              <div id="lessonform" className={styles.lessonView}>
                <div className={styles.lessonSubView}>
                  {!assessments && (
                    <div className={styles.lessonTitle}>Lessons:</div>
                  )}
                  <div className={styles.lessonListView}>
                    {cpres.map((item, index: number) => {
                      if (!item?.id) {
                        return null;
                      }
                      const preLesson: Lesson | undefined =
                        index === 0
                          ? undefined
                          : (cpres.at(index - 1) as Lesson);
                      const nextLesson: Lesson | undefined =
                        index === cpres.length - 1
                          ? undefined
                          : (cpres.at(index + 1) as Lesson);
                      const lessonData: Lesson = item as Lesson;
                      const updatedLessonInfo = {
                        ...lessonInfo,
                        lesson_name: lessonData.name,
                        lesson_id: lessonData.id,
                        lesson_position: lesson?.number || 0,
                        schoolId: user?.schools?.[0]?.id,
                        schoolName: user?.schools?.[0]?.name,
                        userId: user?.id,
                        userName: `${user?.firstName} ${user?.lastName}`,
                      };
                      let preRequiredLessonNumbers: number[] = [];

                      for (let i = index; i > 0; i--) {
                        const curLes = cpres.at(i) as Lesson,
                          preLes = cpres.at(i - 1) as Lesson;
                        if (
                          !curLes.prerequisite &&
                          preLes.status !==
                            (isHomeEDApp ? "confirmed" : "completed")
                        ) {
                          preRequiredLessonNumbers.unshift(i);
                        } else {
                          break;
                        }
                      }

                      const incompleted =
                        heaLessonsStatus?.heaGetLessonStatus?.find(
                          (lesson) => lesson.lessonId === item.id,
                        )?.incomplete ?? false;
                      return (
                        <div
                          id={item.id}
                          key={index}
                          className={styles.lessonItemView}
                        >
                          <LessonForm
                            index={index}
                            lessonCount={cpres.length}
                            lesson={lessonData}
                            preLesson={preLesson}
                            nextLesson={nextLesson}
                            openDetails={handleOpenLesson}
                            openStudyPlanDetails={handleStudyPlanLessonOpen}
                            openLessonWithChilds={handleOpenLessonWithChilds}
                            openPreview={handleLessonPreview}
                            openTeacherFeedback={handleTeacherFeedback}
                            unitId={curExtendedUnit?.id || unitId}
                            lessonInfo={updatedLessonInfo}
                            isParentApp={isParentApp}
                            isLocked={locked}
                            studyPlansForSelectedUnit={
                              studyPlansForSelectedUnit
                            }
                            studyPlan={studyPlan}
                            getHeaLessonCompletedStatus={
                              getHeaLessonCompletedStatus
                            }
                            getHeaLessonStatusLoading={
                              getHeaLessonStatusLoading
                            }
                            studyMode={studyMode}
                            isParentUnit={heaUnitData?.ageGroup?.includes(
                              "Parent & Caregiver",
                            )}
                            exitTicket={exitTickets.find(
                              (ticket) => ticket.lessonNum === item.number,
                            )}
                            handleGenerateTicket={handleGenerateTicket}
                            handleCancelExitTicket={handleCancelExitTicket}
                            movedScroll={movedScroll}
                            isOnlyNotCompletedLesson={
                              (lessonData as any)?.isOnlyNotCompletedLesson
                            }
                            openConfirmPassword={handleShowConfirmPasswordModal}
                            preRequiredLessonNumbers={preRequiredLessonNumbers}
                            incompleted={incompleted}
                            openExitTicket={handleOpenExitTicket}
                            reviewNow={handleReviewNow}
                            reviewLesson={handleReviewLesson}
                            confirmNow={handleConfirmLesson}
                            lessonConfirming={confirming}
                            openAppravalPendingModal={
                              handleShowApprovalPendingModal
                            }
                            handleResumeMenu={handleResumeMenu}
                            handleStartBeginMenu={handleStartBeginMenu}
                            unitUniqueName={curUnitContent?.uniqueName}
                            handleDownloadMaterial={handleShowMaterial}
                            // showMaterailView={showMaterailView}
                            handleOpenShareModal={handleOpenShareModal}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )
          ) : (
            <div id="lessonform" className={styles.lessonView}>
              <div className={styles.lessonSubView}>
                {!assessments && (
                  <div className={styles.lessonTitle}>Lessons:</div>
                )}
                <div className={styles.lessonListView}>
                  {cpres.map((item, index: number) => {
                    if (!item?.id) {
                      return null;
                    }
                    const preLesson: Lesson | undefined =
                      index === 0 ? undefined : (cpres.at(index - 1) as Lesson);
                    const nextLesson: Lesson | undefined =
                      index === cpres.length - 1
                        ? undefined
                        : (cpres.at(index + 1) as Lesson);
                    const lessonData: Lesson = item as Lesson;
                    const updatedLessonInfo = {
                      ...lessonInfo,
                      lesson_name: lessonData.name,
                      lesson_id: lessonData.id,
                      lesson_position: lesson?.number || 0,
                      schoolId: user?.schools?.[0]?.id,
                      schoolName: user?.schools?.[0]?.name,
                      userId: user?.id,
                      userName: `${user?.firstName} ${user?.lastName}`,
                    };
                    let preRequiredLessonNumbers: number[] = [];

                    for (let i = index; i > 0; i--) {
                      const curLes = cpres.at(i) as Lesson,
                        preLes = cpres.at(i - 1) as Lesson;
                      if (
                        !curLes.prerequisite &&
                        preLes.status !==
                          (isHomeEDApp ? "confirmed" : "completed")
                      ) {
                        preRequiredLessonNumbers.unshift(i);
                      } else {
                        break;
                      }
                    }

                    const incompleted =
                      heaLessonsStatus?.heaGetLessonStatus?.find(
                        (lesson) => lesson.lessonId === item.id,
                      )?.incomplete ?? false;
                    return (
                      <div
                        id={item.id}
                        key={index}
                        className={styles.lessonItemView}
                      >
                        <NewLayoutLessonForm
                          index={index}
                          lessonCount={cpres.length}
                          lesson={lessonData}
                          preLesson={preLesson}
                          nextLesson={nextLesson}
                          openDetails={handleOpenLesson}
                          openPreview={handleLessonPreview}
                          openTeacherFeedback={handleTeacherFeedback}
                          unitId={unitId}
                          lessonInfo={updatedLessonInfo}
                          isParentApp={isParentApp}
                          exitTicket={exitTickets.find(
                            (ticket) => ticket.lessonNum === item.number,
                          )}
                          handleGenerateTicket={handleGenerateTicket}
                          handleCancelExitTicket={handleCancelExitTicket}
                          movedScroll={movedScroll}
                          isOnlyNotCompletedLesson={
                            (lessonData as any)?.isOnlyNotCompletedLesson
                          }
                          preRequiredLessonNumbers={preRequiredLessonNumbers}
                          incompleted={incompleted}
                          openExitTicket={handleOpenExitTicket}
                          handleResumeMenu={handleResumeMenu}
                          handleStartBeginMenu={handleStartBeginMenu}
                          unitUniqueName={curUnitContent?.uniqueName}
                          handleDownloadMaterial={handleShowMaterial}
                          // showMaterailView={showMaterailView}
                          handleOpenShareModal={handleOpenShareModal}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!isHomeEDApp && studyGuides.length ? (
            <div
              id="studyguideform"
              className={styles.teacherView}
              style={{ paddingLeft: "24px", paddingRight: "24px" }}
            >
              <div className={styles.teacherSubView}>
                {curUnitContent ? (
                  <StudyGuide
                    unit={curUnitContent}
                    openPreview={handleOpenStudyGuidePreview}
                    startStudyGuide={handleStartStudyGuide}
                    handleDownloadMaterial={handleShowMaterial}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          {showStudyGuidePreview && studyGuides.length ? (
            <div
              className={styles.teacherFSView}
              style={{ paddingLeft: "0px", paddingRight: "0px" }}
            >
              {<div className={styles.fsBackdrop} onClick={handleClose} />}
              <div className={styles.teacherFSSubView}>
                <div>
                  {curExtendedUnit ? (
                    <StudyGuidePreView
                      unit={curExtendedUnit}
                      onClose={handleCloseStudyGuidePreview}
                      startStudyGuide={handleStartStudyGuide}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}

          <div className={styles.empty}>&nbsp;</div>
          {showLessonView && selectedContent ? (
            <div className={styles.teacherFSView}>
              <div className={styles.fsBackdrop} onClick={handleClose} />
              <div className={styles.lessonFSSubView}>
                <SelectedContent
                  lesson={lesson}
                  content={selectedContent}
                  unit={curExtendedUnit}
                  goBack={handleCloseLesson}
                  lastPageNum={lastPageNum}
                  isParentApp={isParentApp}
                  studyPlanId={studyPlan?.id}
                />
              </div>
            </div>
          ) : null}
          {showMaterail && selectedContent ? (
            <div className={styles.teacherFSView}>
              <div className={styles.fsBackdrop} onClick={handleClose} />
              <div className={styles.teacherFSSubView}>
                <MaterialPreview needPrint={needPrint} onClose={handleClose} />
              </div>
            </div>
          ) : null}
          {showLessonPreView && selectedContent && curExtendedUnit && lesson ? (
            <div className={styles.teacherFSView}>
              <div
                className={styles.fsBackdrop}
                onClick={handleCloseLessonPreview}
              />
              <div
                className={
                  newLessonLayout
                    ? styles.newLayoutFSSubView
                    : styles.teacherFSSubView
                }
              >
                <LessonPreView
                  unit={curExtendedUnit}
                  lesson={lesson}
                  onClose={handleCloseLessonPreview}
                  onExit={onClose}
                  startLesson={handleStartLessons}
                  isParentApp={isParentApp}
                  studyPlansForSelectedUnit={filteredStudyPlans}
                  startExercise={handleStartExercise}
                  addUnitToStudyPlan={addUnitToStudyPlan}
                  openDetails={handleReviewNow}
                  studyMode={studyMode}
                  startShortClass={handleStartLessonWithMulti}
                  handleResumeMenu={handleResumeMenu}
                />
              </div>
            </div>
          ) : null}
          {showLessonFeedback ? (
            <div className={styles.teacherFSView}>
              <div
                className={styles.fsBackdrop}
                onClick={handleCloseLessonFeedback}
              />
              <div className={styles.teacherFSSubViewTypeForm}>
                <Widget
                  id={"Yg59zfcB"}
                  hidden={{
                    account: user?.customer?.name ?? "",
                    school: user?.schools?.[0]?.name ?? "",
                    course: program?.name || currentProgram?.name || "",
                    unit: unitData?.name ?? "",
                    country: unitData?.country ?? "",
                    lesson: lesson?.name ?? "",
                    email: heaUser ? heaUser.email : (user?.email ?? ""),
                  }}
                  onSubmit={handleCloseLessonFeedback}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
      {
        <div
          className={styles.backTipView}
          style={{ display: backHover ? "flex" : "none" }}
        >
          <div className={styles.tipTextView}>
            <div className={styles.tipTitle}>{backTitle}</div>
          </div>
          <div className={styles.arrowDown}></div>
        </div>
      }
      {!(isHomeEDApp ? loading2 || locked : loading) && (
        <div className={styles.backButtonView}>
          <div
            className={styles.floatingBack}
            onClick={handleClose}
            onMouseOver={onBackMouseOver}
            onMouseLeave={onBackMouseLeave}
          >
            <FSBackIcon width={36} height={36} />
          </div>
        </div>
      )}
      {resumeModal ? (
        <div className={styles.resumeLessonView}>
          <div className={styles.fsBackdrop} onClick={handleCloseResumeModal} />
          <div className={styles.resumeLessonSubView}>
            <ResumeModal
              onClose={handleCloseResumeModal}
              onContinue={handleResumeLesson}
              onStart={handleStartBegin}
            />
          </div>
        </div>
      ) : null}
      {isHomeEDApp &&
        !loading2 &&
        !locked &&
        (filteredStudyPlans?.length !== 0 || isEditMode) && (
          <div className={styles.studyplanView}>
            <div className={styles.studyListView}>
              <div className={styles.plansTitle}>
                {studyMode !== ModeTypeEnum.Study ? (
                  <p>
                    Already Added <br />
                    to Study Plans:
                  </p>
                ) : (
                  <p>
                    {studyPlan?.name}'s
                    <br /> Study Plan
                  </p>
                )}
              </div>
              {filteredStudyPlans.map((item, i) => {
                return (
                  <Tooltip
                    placement="right"
                    title={
                      studyMode !== ModeTypeEnum.Study && (
                        <div className={styles.lessonTip} key={i}>
                          {studyMode === ModeTypeEnum.Units ||
                          studyMode === ModeTypeEnum.Edit
                            ? "View"
                            : "Start"}{" "}
                          {`${item.name}'s Study Plan`}
                        </div>
                      )
                    }
                    arrowPointAtCenter
                    color="#FFFFFF"
                    key={i}
                  >
                    <div
                      key={i}
                      className={styles.addItem}
                      style={{
                        background: `${item.darkColor}`,
                        cursor:
                          studyMode === ModeTypeEnum.Study
                            ? "default"
                            : "pointer",
                      }}
                      onClick={handleClickStudyPlan(item)}
                    >
                      <div className={styles.addTitle}>
                        {item.name.substring(0, 2).toLocaleUpperCase()}
                      </div>
                    </div>
                  </Tooltip>
                );
              })}
              {isEditMode && (
                <Button
                  label="+"
                  color="white"
                  size="small"
                  className={styles.addStudyPlanBtn}
                  onClick={addUnitToStudyPlan}
                />
              )}
            </div>
          </div>
        )}
      {activeModal && (
        <NewPasswordModal
          open={activeModal}
          studyPlan={studyPlan}
          onClose={handleCloseActiveModal}
          onSuccess={handleSetPassword}
        />
      )}
      {lockModal && (
        <LockModal
          studyPlan={studyPlan}
          open={lockModal}
          onClose={handleCloseLockModal}
          onContinue={handleActivated}
        />
      )}
      {activated && (
        <SuccessModal
          open={activated}
          studyPlan={studyPlan?.name}
          onClose={() => setActivated(false)}
          onSuccess={handleActiveSuccess}
        />
      )}
      {lessonRunModal && isHomeEDApp ? (
        <LessonRunModal
          open={lessonRunModal}
          studyPlansForSelectLesson={filteredStudyPlans}
          state={state}
          onClose={handleCloseLessonModal}
          onContinueWithYou={handleOpenLessonWithYou}
          onContinueWithChild={handleLockWithStudyPlan}
        />
      ) : (
        <MultiLessonRunModal
          open={lessonRunModal}
          onClose={handleCloseLessonModal}
          onContinueWithLonger={handleStartLessonWithLonger}
          onContinueWithMulti={handleStartLessonWithMulti}
        />
      )}
      {alternateLessonRunModal && (
        <AlternateLessonRun
          open={alternateLessonRunModal}
          onClose={() => setAlternateLessonRunModal(false)}
          onStartAlternateLesson={handleStartAlternateLesson}
        />
      )}
      {showExitModal && (
        <GenerateExitTicketLinkModal
          open={showExitModal}
          onClose={() => setShowExitModal(false)}
          onActiveExitTicket={handleActiveFormPreview}
          formData={formData}
          gradeName={currentProgram?.name.replace(/\s*Program/, "")}
        />
      )}
      {showConfirmCompleteModal && (
        <ConfirmPasswordModal
          open={showConfirmCompleteModal}
          lesson={lesson}
          onConfirmPassword={handleConfirmPassword}
          onClose={handleClosePasswordModal}
        />
      )}
      {showReview && (
        <ReviewModal
          open={showReview}
          onClose={handleCloseReviewModal}
          onReviewLater={handleReviewLater}
          onReviewNow={handleReviewNow}
        />
      )}
      {showPendingLessons && (
        <CompletedLessonsModal
          open={showPendingLessons}
          onClose={handleClosePendingModal}
          heaUnits={heaUnits}
          studyPlans={studyPlans}
        />
      )}
      {showExerciseModal && (
        <AdditionalExerciseModal
          open={showExerciseModal}
          onClose={handleCloseExerciseModal}
          lesson={lesson}
          onStartIntroduction={handleStartIntroduction}
          onStartExercise={handleStartExercise}
        />
      )}
      {showInstructionModal && lesson && (
        <RequiredUnitLessonMaterials
          open={showInstructionModal}
          lesson={lesson}
          onClose={() => setShowInstructionModal(false)}
          onStartLesson={handleStartLesson}
          openMaterial={handleMaterialPreview}
          handleDownloadMaterial={handleShowMaterial}
        />
      )}
      {showGuideSessionModal && (
        <InstructionLessonRun
          open={showGuideSessionModal}
          onClose={() => {
            setShowGuideSessionModal(false);
            setIsStudyGuide(false);
          }}
          onStartLesson={handleStartStudyGuide}
          openMaterial={handleOpenTeacher}
          title="Important Lesson Instructions"
          subTitle={
            <div>
              The <strong>Unit Study Review</strong> requires the{" "}
              <strong>Teacher Study Guide</strong>.
            </div>
          }
          downloadLabel="Download Teacher Study Guide"
          lastContent="The Teacher Study Guide can be downloaded below."
        />
      )}
      {openShareModal && (
        <ShareModal
          open={openShareModal}
          title={`Share ${sharedObject.__typename} with`}
          isSll={false}
          shareId={sharedObject?.id}
          onClose={handleCloseShareModal}
          continueWithTeacher={handleShareTeacher}
          continueWithStudent={handleShareStudent}
        />
      )}
      {showStudentLinkModal && (
        <GenerateStudentLinkModal
          open={showStudentLinkModal}
          isSll={false}
          lessonId={sharedObject?.id}
          onClose={() => setShowStudentLinkModal(false)}
          onActiveStudentLink={handleActiveStudentLink}
          formData={formData}
          unitId={unitId}
          unitName={unitName}
          programId={program?.id || currentProgram?.id}
          programName={program?.name || currentProgram?.name}
          gradeName={currentProgram?.name.replace(/\s*Program/, "")}
        />
      )}
      {shareLesson && sharedObject && (
        <NormalModal
          logoIcon={<GreenShareIcon />}
          onClose={handleCloseShareModal}
          title={
            <div
              className={styles.modalTitle}
            >{`Share This ${sharedObject.__typename}`}</div>
          }
          open={shareLesson}
          description={
            <div className={styles.modalDetail}>
              {assessments || isPostProgram
                ? `Use this link to share this ${sharedObject.__typename} with a parent or caregiver. They do not require an Open Parachute account to access this ${sharedObject.__typename}.`
                : `Use this link to share this ${sharedObject.__typename} with a colleague. They will need an Open Parachute account to access the ${sharedObject.__typename}.`}
            </div>
          }
          leftButton={
            <Button
              className={styles.cancelButton}
              onClick={handleCloseShareModal}
              label="Cancel"
            />
          }
          rightButton={
            <CopyToClipboardComponent
              text={createPrettyURL(
                sharedObject.__typename === "Lesson"
                  ? `${curUnitContent?.uniqueName}/${sharedObject?.uniqueName ?? ""}`
                  : `${curUnitContent?.uniqueName}`,
              )}
            >
              <Button
                className={styles.copyButton}
                onClick={handleCopyToClipboard}
                label="Copy Link"
              />
            </CopyToClipboardComponent>
          }
        />
      )}
    </UnitModal>
  );
}

const UnitProgressModal = (props: Props) => {
  const {
    open,
    unitId,
    program,
    onClose,
    unitData,
    heaUnitData,
    isDashboard,
    isParentApp,
    isEditMode,
    parentSkill,
    studyPlansForSelectedUnit,
    addUnitToStudyPlan,
    studyMode,
    currentStudyPlan,
    gotoStudyMode,
    studyPlans,
    heaUnits,
    assessments,
    quizUnit,
    lessonInfoByUniqueName,
    isPostProgram,
  } = props;

  return (
    <CourseContentProvider>
      <UnitProgressComponent
        open={open}
        unitId={unitId}
        program={program}
        onClose={onClose}
        unitData={unitData}
        heaUnitData={heaUnitData}
        assessments={assessments}
        isDashboard={isDashboard}
        isParentApp={isParentApp}
        parentSkill={parentSkill}
        isEditMode={isEditMode}
        studyPlansForSelectedUnit={studyPlansForSelectedUnit}
        addUnitToStudyPlan={addUnitToStudyPlan}
        studyPlans={studyPlans}
        heaUnits={heaUnits}
        studyMode={studyMode}
        currentStudyPlan={currentStudyPlan}
        gotoStudyMode={gotoStudyMode}
        quizUnit={quizUnit}
        lessonInfoByUniqueName={lessonInfoByUniqueName}
        isPostProgram={isPostProgram}
      />
    </CourseContentProvider>
  );
};

export default UnitProgressModal;
